import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserDashboardService, ChartType, Metric, CriteriaOption, ChartData, Dimension, extractChartAuditFields } from '../../../services/user-dashboard.service';
import { NotificationService } from '../../../services/notification.service';
import { FormSelectDropdownComponent } from '../../../components/form-select-dropdown/form-select-dropdown.component';
import { ChartFilterSectionWrapperComponent } from '../chart-filter-section-wrapper/chart-filter-section-wrapper.component';
import { DropdownObjectType } from '../../../shared/types';
import * as Types from '../../../shared/types';
import { convertToFilterCondition, transformTestToFilters } from '../../../shared/appUtil';
import { TIMEZONE_LIST } from '../../../shared/constant';
import { normalizeCategoryArray, normalizeChartDisplayLabel } from '../../../shared/chart-label.util';
import { checkCustomDateRange, CUSTOM_RANGE_MAX_DAYS, maxEndDateFor } from './custom-date-range.util';
import { chartQueryFieldsFromApi } from '../../../shared/chart-insights-drilldown.util';
import dayjs from 'dayjs';

@Component({
  selector: 'add-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, FormSelectDropdownComponent, ChartFilterSectionWrapperComponent],
  templateUrl: './add-chart.component.html',
  styleUrl: './add-chart.component.scss'
})
export class AddChartComponent implements OnInit, OnChanges {
  @Input() showTemplatesOnly: boolean = false;
  @Input() selectedSlot: number | null = null;
  @Input() existingChart: ChartData | null = null;
  @Input() preselectedChartTypeId: string | null = null; // Chart type ID to pre-select when form opens
  @Input() chartId: string | null = null; // Chart ID for editing (if provided, will fetch chart data)
  @Output() chartAdded = new EventEmitter<ChartData>();
  @Output() chartUpdateFailed = new EventEmitter<string>(); // chartId when generate/update API fails (non-200)
  @Output() close = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<void>();
  @Output() chartSelected = new EventEmitter<string>();

  chartTypes: ChartType[] = [];
  filteredChartTypes: ChartType[] = [];
  selectedChartType: ChartType | null = null;
  selectedChartTypeId: string = ''; // For dropdown binding
  selectedChartTypeRaw: any = null; // Store raw API response for supported_metrics
  metrics: Metric[] = [];
  criteriaOptions: CriteriaOption[] = [];
  dimensions: Dimension[] = []; // Dimensions loaded from API
  isLoadingDimensions: boolean = false; // Track dimensions loading state
  searchQuery: string = '';
  isLoading: boolean = false;
  isSavingChart: boolean = false; // True only while save/generate/update chart request is in progress
  isEditingChart: boolean = false; // Flag to prevent form reset when editing
  currentChartId: string | null = null; // Store the chart ID being edited
  existingChartRequest: any = null; // Store the original chart request when editing (to preserve x_axis_name, y_axis_name, etc.)
  targetMetricType: string | null = null; // Store the target metric type to restore after API calls
  isEditMode: boolean = false; // Flag to keep chart type disabled during entire edit session

  // Form fields
  chartTitle: string = '';
  selectedMetric: string = '';
  selectedDimension: string = '';
  selectedSecondaryDimension: string | null = null;
  selectedFilterField: string = '';
  selectedFilterOperator: string = '';
  selectedFilterValue: string = '';
  selectedMetricType: string = '';
  criteria: any = {
    top_n: 10,
    time_bucket: 'day',
    sort_order: 'desc',
    show_percentage: true  // Show Labels enabled by default for pie/donut
  };
  startDate: string = '';
  endDate: string = '';
  dateRangeType: 'custom_range' | 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year' | 'last_n_days' | null = 'today';
  lastNDays: number | null = null;
  /** Initial filters in FilterCondition format (from chart FilterRequest[] when editing). */
  initialFilterConditions: Types.FilterCondition[] = [];

  /** Whether the Optional fields section is expanded (collapsible). */
  optionalSectionExpanded = false;

  @ViewChild(ChartFilterSectionWrapperComponent) chartFilterWrapper?: ChartFilterSectionWrapperComponent;

  toggleOptionalSection(): void {
    this.optionalSectionExpanded = !this.optionalSectionExpanded;
  }

  // Flag to prevent date clearing during form population
  private isPopulatingForm: boolean = false;
  private _lastFormPopulationTime: number = 0;
  private _initialStartDate: string = '';
  private _initialEndDate: string = '';
  private _populationId: number = 0; // Unique ID for each population operation
  get today(): string {
    return dayjs().format('YYYY-MM-DD');
  }

  /** Last selectable end date: start + 90 days. Future dates are allowed; the span is still capped. */
  get maxEndDate(): string {
    return maxEndDateFor(this.startDate);
  }

  /** Hint under the custom range when it reaches into the future (charts show the data available so far). */
  get futureRangeHint(): string {
    const check = checkCustomDateRange(this.startDate, this.endDate);
    if (check.entirelyFuture) return 'This range is entirely in the future, so there is no data yet. The chart stays empty until data arrives.';
    if (check.includesFuture) return 'This range includes future dates. The chart shows the data available so far.';
    return '';
  }

  // Minimum date for start date picker (2023-01-01)
  get minDate(): string {
    return '2000-01-01';
  }

  // Filter operators
  filterOperators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Not Contains' },
    { value: 'in', label: 'In' },
    { value: 'not_in', label: 'Not In' }
  ];

  chartCategories: { [key: string]: ChartType[] } = {};

  // Dropdown options for form-select-dropdown components
  chartTypeOptions: DropdownObjectType[] = [];
  selectedChartTypeOption: DropdownObjectType | null = null;
  metricOptions: DropdownObjectType[] = [];
  selectedMetricOption: DropdownObjectType | null = null;
  dimensionOptions: DropdownObjectType[] = [];
  selectedDimensionOption: DropdownObjectType | null = null;
  selectedSecondaryDimensionOption: DropdownObjectType | null = null;
  dateRangeOptions: DropdownObjectType[] = [];
  selectedDateRangeOption: DropdownObjectType | null = null;
  timezoneOptions: DropdownObjectType[] = [];
  selectedTimezoneOption: DropdownObjectType | null = null;
  /** Time zone for chart data (e.g. EST). Default EST per Insights dashboard. */
  selectedTimezone: string = 'EST';
  filterFieldOptions: DropdownObjectType[] = [];
  selectedFilterFieldOption: DropdownObjectType | null = null;
  filterOperatorOptions: DropdownObjectType[] = [];
  selectedFilterOperatorOption: DropdownObjectType | null = null;

  constructor(
    private userDashboardService: UserDashboardService,
    private notificationService: NotificationService
  ) {}

  ngOnInit() {
    //console.log('DEBUG: ngOnInit called - isEditMode:', this.isEditMode, 'isEditingChart:', this.isEditingChart, 'chartId:', this.chartId, 'existingChart:', !!this.existingChart);
    // Load dimensions from API
    this.loadDimensions();

    // Load chart types - always needed for both templates and form
    this.loadChartTypes();

    // Initialize dates based on initial date range type for new charts
    if (!this.isEditMode && !this.isPopulatingForm) {
      //console.log('DEBUG: Initializing dates for new chart - dateRangeType:', this.dateRangeType);
      //console.log('DEBUG: Date initialization conditions - isEditMode:', this.isEditMode, 'isPopulatingForm:', this.isPopulatingForm);
      if (this.dateRangeType === 'custom_range') {
        this.startDate = '';
        this.endDate = '';
      } else {
        // Set default dates for non-custom ranges
        this.startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
        this.endDate = dayjs().format('YYYY-MM-DD');
      }
      //console.log('DEBUG: Dates after initialization - startDate:', this.startDate, 'endDate:', this.endDate);
    } else {
      //console.log('DEBUG: Skipping date initialization - isEditMode:', this.isEditMode, 'isPopulatingForm:', this.isPopulatingForm);
    }

    // Store initial values to detect if they get overridden later
    this._initialStartDate = this.startDate;
    this._initialEndDate = this.endDate;

    // Priority: chartId (API-based editing) > existingChart (local data) > preselectedChartTypeId
    if (this.chartId) {
      // If chartId is provided, always fetch from API for editing (most reliable)
      this.currentChartId = this.chartId;
      this.fetchChartForEditing(this.chartId);
    } else if (this.existingChart) {
      // If existingChart is provided directly (fallback), use it
      this.currentChartId = this.existingChart.id || null;
      this.populateFormFromChart(this.existingChart);
    } else if (this.preselectedChartTypeId && this.chartTypes.length > 0) {
      // If there's a preselected chart type and chart types are already loaded, select it
      this.selectChartTypeById(this.preselectedChartTypeId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Priority: chartId (API-based editing) > existingChart (local data)
    // If chartId input changes, always fetch chart data from API (most reliable)
    if (changes['chartId'] && this.chartId) {
      this.currentChartId = this.chartId;
      this.fetchChartForEditing(this.chartId);
      return; // Don't process existingChart if chartId is available
    }

    // If existingChart input changes and no chartId, populate the form from local data
    if (changes['existingChart'] && this.existingChart && !this.chartId) {
      this.currentChartId = this.existingChart.id || null;
      this.populateFormFromChart(this.existingChart);
    }

    // If preselectedChartTypeId changes and chart types are loaded, select it
    if (changes['preselectedChartTypeId'] && this.preselectedChartTypeId && this.chartTypes.length > 0 && !this.chartId && !this.existingChart) {
      this.selectChartTypeById(this.preselectedChartTypeId);
    }
  }

  // Fetch chart data from API for editing
  fetchChartForEditing(chartId: string) {
    this.isLoading = true;
    this.userDashboardService.getChartForEditing(chartId).subscribe({
      next: (response: any) => {

        // Handle wrapped API response - check for response.data first
        let chartRequest: any;
        if (response && response.data) {
          chartRequest = response.data;
        } else {
          chartRequest = response;
        }

        const metricType = chartRequest.metric_type || chartRequest.metricType;

        // Store the original chart request to preserve fields like x_axis_name, y_axis_name
        this.existingChartRequest = chartRequest;
        // Store the target metric type to restore after API calls
        this.targetMetricType = metricType || null;

        // Set editing flag
        this.isEditingChart = true;
        this.isEditMode = true; // Set edit mode flag
        this.currentChartId = chartId;

        // Extract and set form values from the request
        if (chartRequest.chart_type) {
          // Wait for chart types to load, then select the chart type
          if (this.chartTypes.length > 0) {
            this.populateFormFromChartRequest(chartRequest);
          } else {
            // Wait for chart types to load
            let attempts = 0;
            const maxAttempts = 50;
            const checkInterval = setInterval(() => {
              attempts++;
              if (this.chartTypes.length > 0) {
                clearInterval(checkInterval);
                this.populateFormFromChartRequest(chartRequest);
              } else if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                // console.warn('Timeout waiting for chart types to load');
                this.isLoading = false;
              }
            }, 100);
          }
        } else {
          this.isLoading = false;
        }
      },
      error: (error: any) => {
        console.error('Error fetching chart for editing:', error);
        this.notificationService.error('Load Failed', 'Failed to load chart data for editing. Please try again.');
        this.isLoading = false;
      }
    });
  }

  // Helper method to set metric from metric type
  setMetricFromType(metricType: string) {

    if (!metricType) {
      // console.warn('No metric type provided');
      return;
    }

    if (this.metrics.length === 0) {
      // console.warn('Metrics array is still empty, cannot set metric');
      return;
    }

    // Try exact match first
    let matchedMetric = this.metrics.find(m => m.id === metricType);

    // If no exact match, try case-insensitive match
    if (!matchedMetric) {
      matchedMetric = this.metrics.find(m => m.id.toLowerCase() === metricType.toLowerCase());
    }

    if (matchedMetric) {
      this.selectedMetric = matchedMetric.id;
      // Set metric dropdown option object
      this.selectedMetricOption = this.metricOptions.find(option => option.value === matchedMetric.id) || null;

      // Verify it was set correctly
      setTimeout(() => {
        if (this.selectedMetric !== matchedMetric.id) {
          // console.warn('⚠ Metric was reset, restoring it again');
          this.selectedMetric = matchedMetric.id;
          // Reset dropdown option as well
          this.selectedMetricOption = this.metricOptions.find(option => option.value === matchedMetric.id) || null;
        }
      }, 50);
    } else {
      if (this.metrics.length > 0) {
        const defaultMetric = this.metrics.find(m => m.id === 'volume') ?? this.metrics[0];
        this.selectedMetric = defaultMetric.id;
        this.selectedMetricOption = this.metricOptions.find(option => option.value === defaultMetric.id) || null;
      }
    }
  }

  // Populate form from chart request (API response format)
  populateFormFromChartRequest(chartRequest: any) {
    //console.log('DEBUG: populateFormFromChartRequest called');
    // Set chart type
    const chartTypeId = chartRequest.chart_type?.toLowerCase();
    if (chartTypeId && this.chartTypes.length > 0) {
      const chartType = this.chartTypes.find(ct =>
        ct.id === chartTypeId || ct.id.toLowerCase() === chartTypeId
      );
      if (chartType) {
        // Use the edit method to prevent form reset
        const restoreCallback = () => {

          this.chartTitle = chartRequest.chart_title || '';

          // Set metric - handle both metric_type and metricType field names
          const metricType = chartRequest.metric_type || chartRequest.metricType;

          if (metricType) {
            // Wait a bit if metrics array is empty
            if (this.metrics.length === 0) {
              // console.warn('Metrics array is empty, waiting for metrics to load...');
              let retryCount = 0;
              const maxRetries = 10;
              const checkMetrics = setInterval(() => {
                retryCount++;
                if (this.metrics.length > 0) {
                  clearInterval(checkMetrics);
                  this.setMetricFromType(metricType);
                } else if (retryCount >= maxRetries) {
                  clearInterval(checkMetrics);
                  console.error('Timeout waiting for metrics to load');
                }
              }, 100);
            } else {
              this.setMetricFromType(metricType);
            }
          } else {
            // console.warn('No metric_type in chartRequest');
          }

          // Set dimensions (option objects synced via syncMetricAndDimensionOptions after restore)
          this.selectedDimension = chartRequest.dimension || '';
          this.selectedSecondaryDimension = chartRequest.secondary_dimension || null;

          // Prevent date clearing during form population
          this.isPopulatingForm = true;
          const currentPopulationId = ++this._populationId;
          //console.log('DEBUG: populateFormFromChartRequest - populationId:', currentPopulationId);

          if (chartRequest.criteria?.date_range_type && chartRequest.criteria.date_range_type !== 'custom_range') {
            // Dynamic date range (excluding 'custom_range' which is handled by start_date/end_date)
            //console.log('DEBUG: Dynamic date range detected - date_range_type:', chartRequest.criteria.date_range_type);
            this.dateRangeType = chartRequest.criteria.date_range_type;
            if (chartRequest.criteria.date_range_type === 'last_n_days' && chartRequest.criteria.last_n_days) {
              this.lastNDays = chartRequest.criteria.last_n_days;
            } else {
              this.lastNDays = null;
            }
            // Clear custom dates when using dynamic range
            //console.log('DEBUG: Setting default dates for dynamic range');
            this.startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
            this.endDate = dayjs().format('YYYY-MM-DD');
            //console.log('DEBUG: Dates after setting defaults for dynamic range - startDate:', this.startDate, 'endDate:', this.endDate);
          } else if (chartRequest.start_date || chartRequest.end_date || chartRequest.criteria?.date_range_type === 'custom_range') {
            // Custom date range - set dates first to prevent clearing
            //console.log('DEBUG: Custom date range detected - start_date:', chartRequest.start_date, 'end_date:', chartRequest.end_date, 'date_range_type:', chartRequest.criteria?.date_range_type);
            if (chartRequest.start_date) {
              // Extract just the date part to avoid timezone issues
              const startDateStr = chartRequest.start_date.split('T')[0];
              this.startDate = startDateStr;
              //console.log('DEBUG: Set startDate from chartRequest:', startDateStr);
            } else if (chartRequest.criteria?.date_range_type === 'custom_range') {
              // For custom_range without explicit dates, don't set defaults - leave empty
              this.startDate = '';
              //console.log('DEBUG: Set empty startDate for custom_range without explicit date');
            } else {
              this.startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
              //console.log('DEBUG: Set default startDate (no start_date in chartRequest)');
            }
            if (chartRequest.end_date) {
              // Extract just the date part to avoid timezone issues
              const endDateStr = chartRequest.end_date.split('T')[0];
              this.endDate = endDateStr;
              //console.log('DEBUG: Set endDate from chartRequest:', endDateStr);
            } else if (chartRequest.criteria?.date_range_type === 'custom_range') {
              // For custom_range without explicit dates, don't set defaults - leave empty
              this.endDate = '';
              //console.log('DEBUG: Set empty endDate for custom_range without explicit date');
            } else {
              this.endDate = dayjs().format('YYYY-MM-DD');
              //console.log('DEBUG: Set default endDate (no end_date in chartRequest)');
            }
            // Set date range type after dates are set
            this.dateRangeType = 'custom_range';
            this.lastNDays = null;
            //console.log('DEBUG: Set dateRangeType to custom_range, dates - startDate:', this.startDate, 'endDate:', this.endDate);
          } else {
            // Default to today if no date range specified
            this.dateRangeType = 'today';
            this.lastNDays = null;
          }

          // Set date range dropdown option object
          if (this.dateRangeType) {
            this.selectedDateRangeOption = this.dateRangeOptions.find(option => option.value === this.dateRangeType) || null;
          }

          // Time zone (default EST per Insights dashboard). API may return snake_case or camelCase.
          const rawTz =
            chartRequest.time_zone ??
            chartRequest.timeZone ??
            chartRequest.TimeZone;
          const tz =
            typeof rawTz === 'string' && rawTz.trim() !== ''
              ? rawTz.trim()
              : 'EST';
          this.selectedTimezone = tz;
          const tzList = this.timezoneOptions.length ? this.timezoneOptions : TIMEZONE_LIST;
          this.selectedTimezoneOption = tzList.find((o: DropdownObjectType) => o.value === tz) ?? tzList.find((o: DropdownObjectType) => o.value === 'EST') ?? tzList[0] ?? null;

          // Re-enable date clearing after form population
          setTimeout(() => {
            //console.log('DEBUG: populateFormFromChartRequest timeout - checking populationId');
            //console.log('DEBUG: currentPopulationId:', currentPopulationId, 'this._populationId:', this._populationId);
            //console.log('DEBUG: Dates before potential reset - startDate:', this.startDate, 'endDate:', this.endDate);

            if (this._populationId === currentPopulationId) {
              //console.log('DEBUG: populateFormFromChartRequest timeout - populationId matches, resetting isPopulatingForm');
              this.isPopulatingForm = false;
              //console.log('DEBUG: isPopulatingForm now:', this.isPopulatingForm);
              //console.log('DEBUG: Dates after reset - startDate:', this.startDate, 'endDate:', this.endDate);

              // Check if dates were immediately overridden
              setTimeout(() => {
                //console.log('DEBUG: Dates 50ms after reset - startDate:', this.startDate, 'endDate:', this.endDate);
              }, 50);
            } else {
              //console.log('DEBUG: populateFormFromChartRequest timeout - populationId mismatch, not resetting isPopulatingForm');
            }
          }, 100);

          // Set filters (FilterRequest[] from API)
          const rawFilters = (chartRequest.filters && Array.isArray(chartRequest.filters) ? chartRequest.filters : []) as Types.FilterRequest[];
          this.initialFilterConditions = rawFilters.length ? convertToFilterCondition(rawFilters) : [];
          // Expand Optional so existing filters are visible when editing
          if (this.initialFilterConditions.length > 0) {
            this.optionalSectionExpanded = true;
          }

          // Set criteria - preserve all criteria fields from the request
          // When editing, missing API top_n stays null (do not default to 10)
          if (chartRequest.criteria && typeof chartRequest.criteria === 'object') {
            this.criteria = {
              top_n: null,
              time_bucket: 'day',
              sort_order: 'desc'
            };
            Object.keys(chartRequest.criteria).forEach(key => {
              const value = chartRequest.criteria[key];
              if (value !== null && value !== undefined && value !== '') {
                this.criteria[key] = value;
              }
            });
          } else {
            this.criteria = {
              top_n: null,
              time_bucket: 'day',
              sort_order: 'desc'
            };
          }

          this.ensurePercentageForPieDonutCharts();

          // Ensure date_range_type is properly set in criteria if it exists
          if (chartRequest.criteria?.date_range_type) {
            this.criteria.date_range_type = chartRequest.criteria.date_range_type;
          }
          // Ensure last_n_days is properly set in criteria if it exists
          if (chartRequest.criteria?.last_n_days) {
            this.criteria.last_n_days = chartRequest.criteria.last_n_days;
          }

          // Clear flag after a delay to ensure UI is updated
          setTimeout(() => {
            this.isEditingChart = false;
            this.isLoading = false;
          }, 200);
        };

        this.onChartTypeSelectForEdit(chartType, restoreCallback);
      } else {
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
    }
  }

  populateFormFromChart(chart: ChartData) {
    //console.log('DEBUG: populateFormFromChart called');
    if (!chart || !chart.config) {
      return;
    }

    // Set editing flag to prevent form reset
    this.isEditingChart = true;
    this.isEditMode = true; // Set edit mode flag

    const config = chart.config;

    // Get chart type - try multiple sources
    // Priority: config.chart_type > config.chart.type > chart.chart_type
    const chartTypeId = (config.chart_type || config.chart?.type || chart.chart_type || '').toLowerCase();
    // Extract chart title - try multiple sources
    const chartTitle = chart.title || config.title?.text || config.chart_title || '';

    // Parse date range from subtitle if available
    // Format: "2023-12-24T18:30:00.000Z to 2026-01-01T18:29:59.999Z"
    let startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
    let endDate = dayjs().format('YYYY-MM-DD');

    // Try to parse from subtitle first
    if (config.subtitle?.text) {
      const subtitleText = config.subtitle.text;
      //console.log('DEBUG: Parsing subtitle:', subtitleText);
      const dateRangeMatch = subtitleText.match(/(\d{4}-\d{2}-\d{2})T[\d:\.Z]+\s+to\s+(\d{4}-\d{2}-\d{2})T[\d:\.Z]+/);
      if (dateRangeMatch) {
        // Extract just the date parts (YYYY-MM-DD) to avoid timezone issues
        startDate = dateRangeMatch[1];
        endDate = dateRangeMatch[2];
        //console.log('DEBUG: Parsed dates from subtitle - startDate:', startDate, 'endDate:', endDate);
      }
    }

    // Fallback to direct config properties if available
    if (config.start_date) {
      // Extract just the date part to avoid timezone issues
      const startDateStr = config.start_date.split('T')[0];
      startDate = startDateStr;
      //console.log('DEBUG: Using config.start_date - startDate:', startDate);
    }

    if (config.end_date) {
      // Extract just the date part to avoid timezone issues
      const endDateStr = config.end_date.split('T')[0];
      endDate = endDateStr;
      //console.log('DEBUG: Using config.end_date - endDate:', endDate);
    }

    // Extract metric type from yAxis title
    // Map common metric names to metric types
    let metricType = config.metric_type || '';
    if (!metricType && config.yAxis?.title?.text) {
      const yAxisTitle = config.yAxis.title.text.toLowerCase();
      // Map common metric names
      if (yAxisTitle.includes('volume')) {
        metricType = 'volume';
      } else if (yAxisTitle.includes('count')) {
        metricType = 'count';
      } else if (yAxisTitle.includes('sentiment')) {
        metricType = 'sentiment_score';
      } else {
        // Try to use the title as-is
        metricType = config.yAxis.title.text.toLowerCase().replace(/\s+/g, '_');
      }
    }

    // Extract dimension - might not be available in Highcharts config
    // Try to infer from chart title pattern like "Metric by Dimension" or "Count by PrimaryTopic"
    let dimension = config.dimension || '';
    if (!dimension && chartTitle) {
      // Try to extract from title patterns like "Volume by Intent", "Count by PrimaryTopic"
      const byPattern = /by\s+([A-Za-z_]+)/i;
      const match = chartTitle.match(byPattern);
      if (match && match[1]) {
        const extractedDimension = match[1];
        // Map common dimension names to database column names
        const dimensionMap: { [key: string]: string } = {
          'intent': 'intent',
          'primarytopic': 'primarytopic',
          'queuename': 'queue_name',
          'queue_name': 'queue_name',
          'agent_username': 'agent_username',
          'rootcause': 'rootcauseclustered',
          'rootcauseclustered': 'rootcauseclustered',
          'CustomerPainPoint': 'customerpainpoint',
          'custom_rangeerpainpointclustered': 'custom_rangeerpainpointclustered',
          'outcome': 'outcomeclustered',
          'outcomeclustered': 'outcomeclustered',
          'trigger': 'triggerclustered',
          'triggerclustered': 'triggerclustered'
        };
        const mappedDimension = dimensionMap[extractedDimension.toLowerCase()];
        if (mappedDimension) {
          dimension = mappedDimension;
        } else {
          // Try to capitalize and use as-is
          dimension = extractedDimension.charAt(0).toUpperCase() + extractedDimension.slice(1);
        }
      }
    }

    let secondaryDimension = config.secondary_dimension !== undefined && config.secondary_dimension !== null && config.secondary_dimension !== ''
      ? config.secondary_dimension
      : null;

    // Extract filters and criteria - check both top-level and config level
    // Filters can be stored at chart.filters (top-level) or config.filters
    let filters: any[] = [];
    if (chart.filters && Array.isArray(chart.filters)) {
      // Top-level filters (preferred)
      filters = [...chart.filters];
    } else if (config.filters && Array.isArray(config.filters)) {
      // Config-level filters (fallback)
      filters = [...config.filters];
    }

    const criteria =
      config.criteria && typeof config.criteria === 'object'
        ? { ...this.criteria, ...config.criteria }
        : this.criteria;
    if (config.criteria && typeof config.criteria === 'object') {
      if (!Object.prototype.hasOwnProperty.call(config.criteria, 'top_n') || config.criteria.top_n === undefined) {
        criteria.top_n = null;
      }
    }

    // Check for dynamic date range in criteria
    let dateRangeType: 'custom_range' | 'today' | 'this_week' | 'this_month' | 'this_quarter' | 'this_year' | 'last_n_days' | null = null;
    let lastNDays: number | null = null;
    if (criteria.date_range_type) {
      dateRangeType = criteria.date_range_type;
      if (criteria.date_range_type === 'last_n_days' && criteria.last_n_days) {
        lastNDays = criteria.last_n_days;
      }
    } else {
      // If no date_range_type in criteria, it's a custom/fixed range
      dateRangeType = 'custom_range';
    }

    // Store all config values before selecting chart type (which resets the form)
    const savedConfig = {
      chartTitle: chartTitle,
      metricType: metricType,
      dimension: dimension,
      secondaryDimension: secondaryDimension,
      startDate: startDate,
      endDate: endDate,
      dateRangeType: dateRangeType,
      lastNDays: lastNDays,
      filters: filters,
      criteria: criteria
    };
    //console.log('DEBUG: Created savedConfig with dates - startDate:', startDate, 'endDate:', endDate, 'dateRangeType:', dateRangeType);

    // Set chart type - wait for chart types to load if not loaded yet
    if (chartTypeId) {
      // Helper function to restore form values after chart type is selected
      const restoreFormValues = () => {
        // Restore all form values
        this.chartTitle = savedConfig.chartTitle;

        // Restore metric - check if it exists in the metrics array
        if (savedConfig.metricType) {
          const metricExists = this.metrics.some(m =>
            m.id === savedConfig.metricType ||
            m.id.toLowerCase() === savedConfig.metricType.toLowerCase()
          );
          if (metricExists) {
            this.selectedMetric = savedConfig.metricType;
            // Set dropdown option object
            this.selectedMetricOption = this.metricOptions.find(option => option.value === savedConfig.metricType) || null;
          } else {
            // console.warn('Metric not found in metrics array', {
            //   metricType: savedConfig.metricType,
            //   availableMetrics: this.metrics.map(m => m.id)
            // });
            // Try to find a similar metric or use the first one
            const similarMetric = this.metrics.find(m =>
              m.id.toLowerCase().includes(savedConfig.metricType.toLowerCase()) ||
              savedConfig.metricType.toLowerCase().includes(m.id.toLowerCase())
            );
            this.selectedMetric = similarMetric ? similarMetric.id : (this.metrics.length > 0 ? this.metrics[0].id : '');
            // Set dropdown option object
            this.selectedMetricOption = this.metricOptions.find(option => option.value === this.selectedMetric) || null;
          }
        }

        // Restore dimensions
        this.selectedDimension = savedConfig.dimension || '';
        this.selectedSecondaryDimension = savedConfig.secondaryDimension;

        // Sync metric/dimension dropdown option objects (match by value or label)
        this.syncMetricAndDimensionOptions();

        // Restore date range - check for dynamic date range first
        // Set dates first to prevent them from being cleared by onDateRangeTypeChange
        //console.log('DEBUG: Before setting dates - startDate:', this.startDate, 'endDate:', this.endDate);
        //console.log('DEBUG: savedConfig dates - startDate:', savedConfig.startDate, 'endDate:', savedConfig.endDate);
        this.startDate = savedConfig.startDate;
        this.endDate = savedConfig.endDate;
        //console.log('DEBUG: After setting dates - startDate:', this.startDate, 'endDate:', this.endDate);


        // Prevent date clearing during form population
        this.isPopulatingForm = true;
        const currentPopulationId = ++this._populationId;
        //console.log('DEBUG: populateFormFromChart - populationId:', currentPopulationId);

        if (savedConfig.dateRangeType) {
          this.dateRangeType = savedConfig.dateRangeType;
          this.lastNDays = savedConfig.lastNDays || null;
        } else if (savedConfig.criteria?.date_range_type) {
          this.dateRangeType = savedConfig.criteria.date_range_type;
          if (savedConfig.criteria.date_range_type === 'last_n_days' && savedConfig.criteria.last_n_days) {
            this.lastNDays = savedConfig.criteria.last_n_days;
          } else {
            this.lastNDays = null;
          }
        } else {
          this.dateRangeType = 'custom_range';
          this.lastNDays = null;
        }

        // Set date range dropdown option object
        if (this.dateRangeType) {
          this.selectedDateRangeOption = this.dateRangeOptions.find(option => option.value === this.dateRangeType) || null;
          //console.log('DEBUG: After dropdown setup - dateRangeType:', this.dateRangeType, 'selectedDateRangeOption:', this.selectedDateRangeOption);
          //console.log('DEBUG: Dates after dropdown setup - startDate:', this.startDate, 'endDate:', this.endDate);
        }

        // Re-enable date clearing after form population with longer delay
        this._lastFormPopulationTime = Date.now();
        setTimeout(() => {
          //console.log('DEBUG: populateFormFromChart timeout - checking populationId');
          //console.log('DEBUG: In timeout before isPopulatingForm=false - startDate:', this.startDate, 'endDate:', this.endDate);
          //console.log('DEBUG: isPopulatingForm was:', this.isPopulatingForm);
          //console.log('DEBUG: currentPopulationId:', currentPopulationId, 'this._populationId:', this._populationId);

          if (this._populationId === currentPopulationId) {
            //console.log('DEBUG: populateFormFromChart timeout - populationId matches, resetting isPopulatingForm');
            this.isPopulatingForm = false;
            //console.log('DEBUG: isPopulatingForm now:', this.isPopulatingForm);
            //console.log('DEBUG: In timeout after isPopulatingForm=false - startDate:', this.startDate, 'endDate:', this.endDate);
          } else {
            //console.log('DEBUG: populateFormFromChart timeout - populationId mismatch, not resetting isPopulatingForm');
          }
        }, 1000); // Longer delay to ensure all cycles complete

        // Restore filters (FilterRequest[])
        const rawFilters = (savedConfig.filters && Array.isArray(savedConfig.filters) ? savedConfig.filters : []) as Types.FilterRequest[];
        this.initialFilterConditions = rawFilters.length ? convertToFilterCondition(rawFilters) : [];
        if (this.initialFilterConditions.length > 0) {
          this.optionalSectionExpanded = true;
        }

        // Re-sync option objects when dimensions/metrics may have loaded after restore
        setTimeout(() => this.syncMetricAndDimensionOptions(), 400);
        setTimeout(() => this.syncMetricAndDimensionOptions(), 800);

        // Leave "add new filter" inputs empty; applied filters are shown in the list and summary
        this.selectedFilterField = '';
        this.selectedFilterValue = '';
        this.selectedFilterOperator = '';
        this.selectedFilterFieldOption = null;
        this.selectedFilterOperatorOption = null;

        // Restore criteria - merge with defaults to ensure all fields are present
        if (savedConfig.criteria && typeof savedConfig.criteria === 'object') {
          this.criteria = {
            top_n: null,
            time_bucket: 'day',
            sort_order: 'desc'
          };
          Object.keys(savedConfig.criteria).forEach(key => {
            const value = savedConfig.criteria[key];
            if (value !== null && value !== undefined && value !== '') {
              this.criteria[key] = value;
            }
          });
        } else {
          this.criteria = {
            top_n: null,
            time_bucket: 'day',
            sort_order: 'desc'
          };
        }

        // Ensure date_range_type is properly set in criteria if it exists in saved config
        if (savedConfig.dateRangeType) {
          this.criteria.date_range_type = savedConfig.dateRangeType;
        } else if (savedConfig.criteria?.date_range_type) {
          this.criteria.date_range_type = savedConfig.criteria.date_range_type;
        }
        // Ensure last_n_days is properly set in criteria if it exists
        if (savedConfig.lastNDays) {
          this.criteria.last_n_days = savedConfig.lastNDays;
        } else if (savedConfig.criteria?.last_n_days) {
          this.criteria.last_n_days = savedConfig.criteria.last_n_days;
        }

        this.ensurePercentageForPieDonutCharts();

        // Clear editing flag after restoration
        this.isEditingChart = false;
      };

      // If chart types are already loaded, select immediately
      if (this.chartTypes.length > 0) {
        const chartType = this.chartTypes.find(ct =>
          ct.id === chartTypeId ||
          ct.id.toLowerCase() === chartTypeId.toLowerCase()
        );
        if (chartType) {
          // Select chart type (this will set selectedChartTypeId and selectedChartType)
          this.onChartTypeSelectForEdit(chartType, restoreFormValues);
        } else {
          // console.warn('Chart type not found in chartTypes array', {
          //   chartTypeId,
          //   availableTypes: this.chartTypes.map(ct => ct.id)
          // });
          // If chart type not found, still set the basic values
          this.selectedChartTypeId = chartTypeId;
          // Try to set a partial chart type object so the form can render
          this.selectedChartType = {
            id: chartTypeId,
            name: chartTypeId,
            description: ''
          };
          // Set chart type dropdown option object
          this.selectedChartTypeOption = this.chartTypeOptions.find(option => option.value === chartTypeId) || null;
          restoreFormValues();
        }
      } else {
        // Wait for chart types to load, then select
        // This handles the case where form is shown before chart types are loaded
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max (50 * 100ms)

        const checkInterval = setInterval(() => {
          attempts++;
          if (this.chartTypes.length > 0) {
            clearInterval(checkInterval);
            const chartType = this.chartTypes.find(ct =>
              ct.id === chartTypeId ||
              ct.id.toLowerCase() === chartTypeId.toLowerCase()
            );
            if (chartType) {
              // Select chart type (this will set selectedChartTypeId and selectedChartType)
              this.onChartTypeSelectForEdit(chartType, restoreFormValues);
            } else {
              // console.warn('Chart type not found after loading', {
              //   chartTypeId,
              //   availableTypes: this.chartTypes.map(ct => ct.id)
              // });
              // If chart type not found, still set the basic values
              this.selectedChartTypeId = chartTypeId;
              // Try to set a partial chart type object so the form can render
              this.selectedChartType = {
                id: chartTypeId,
                name: chartTypeId,
                description: ''
              };
              restoreFormValues();
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(checkInterval);
            // console.warn('Timeout waiting for chart types to load');
            // Set basic values anyway
            this.selectedChartTypeId = chartTypeId;
            // Try to set a partial chart type object so the form can render
            this.selectedChartType = {
              id: chartTypeId,
              name: chartTypeId,
              description: ''
            };
            restoreFormValues();
          }
        }, 100);
      }
    } else {
      // console.warn('No chart type ID found in config');
      // If no chart type, just set the values directly
      this.chartTitle = savedConfig.chartTitle;
      this.selectedMetric = savedConfig.metricType;
      this.selectedDimension = savedConfig.dimension || '';
      this.selectedSecondaryDimension = savedConfig.secondaryDimension;
      this.startDate = savedConfig.startDate;
      this.endDate = savedConfig.endDate;

      // Restore filters (FilterRequest[])
      const rawFilters = (savedConfig.filters && Array.isArray(savedConfig.filters) ? savedConfig.filters : []) as Types.FilterRequest[];
      this.initialFilterConditions = rawFilters.length ? convertToFilterCondition(rawFilters) : [];
      if (this.initialFilterConditions.length > 0) {
        this.optionalSectionExpanded = true;
      }

      // Restore criteria with proper defaults
      if (savedConfig.criteria && typeof savedConfig.criteria === 'object') {
        this.criteria = {
          top_n: null,
          time_bucket: 'day',
          sort_order: 'desc',
          ...savedConfig.criteria
        };
        Object.keys(this.criteria).forEach(key => {
          if (this.criteria[key] === null || this.criteria[key] === undefined || this.criteria[key] === '') {
            delete this.criteria[key];
          }
        });
        if (!('top_n' in this.criteria)) {
          this.criteria.top_n = null;
        }
      } else {
        this.criteria = {
          top_n: null,
          time_bucket: 'day',
          sort_order: 'desc'
        };
      }

      // Ensure date_range_type is properly set in criteria if it exists in saved config
      if (savedConfig.dateRangeType) {
        this.criteria.date_range_type = savedConfig.dateRangeType;
      } else if (savedConfig.criteria?.date_range_type) {
        this.criteria.date_range_type = savedConfig.criteria.date_range_type;
      }
      // Ensure last_n_days is properly set in criteria if it exists
      if (savedConfig.lastNDays) {
        this.criteria.last_n_days = savedConfig.lastNDays;
      } else if (savedConfig.criteria?.last_n_days) {
        this.criteria.last_n_days = savedConfig.criteria.last_n_days;
      }
    }
  }

  // New method to handle chart type selection when editing (doesn't reset form values)
  onChartTypeSelectForEdit(chartType: ChartType, restoreCallback: () => void) {
    // Set editing flag to prevent dropdown change from resetting form
    this.isEditingChart = true;

    // Set chart type properties
    this.selectedChartType = chartType;
    // Set the ID - this will trigger ngModelChange, but our flag will prevent reset
    this.selectedChartTypeId = chartType.id;
    // Store raw chart data if available
    this.selectedChartTypeRaw = (chartType as any).rawData || null;

    // Set chart type dropdown option object
    this.selectedChartTypeOption = this.chartTypeOptions.find(option => option.value === chartType.id) || null;

    // Don't reset form when editing - we'll restore values after loading details
    // Load chart details when chart type is selected
    this.loadChartDetailsForEdit(chartType.id, restoreCallback);
  }

  // Modified version of loadChartDetails that accepts a callback to restore form values
  loadChartDetailsForEdit(chartTypeId: string, restoreCallback: () => void) {
    this.isLoading = true;
    this.metrics = [];
    this.criteriaOptions = [];
    // Don't reset selectedMetric - preserve it if we have targetMetricType
    if (!this.targetMetricType) {
      this.selectedMetric = '';
    }
    // Track loading state for both async operations
    let metricsLoaded = false;
    let criteriaLoaded = false;
    let metricsRetryCount = 0;
    const maxMetricsRetries = 5;

    const checkAndRestore = () => {

      // Ensure metrics array is populated before restoring
      if (metricsLoaded && criteriaLoaded && this.metrics.length > 0) {
        this.isLoading = false;
        // Restore form values after a short delay to ensure UI is updated
        // Use a longer delay to ensure metrics array is stable
        setTimeout(() => {
          restoreCallback();
          // Sync dropdown option objects so metric/dimension display correctly
          this.syncMetricAndDimensionOptions();

          // Ensure metric is set after restoration - use targetMetricType as source of truth
          setTimeout(() => {
            if (this.targetMetricType && (!this.selectedMetric || this.selectedMetric !== this.targetMetricType)) {
              this.setMetricFromType(this.targetMetricType);
            }
            this.syncMetricAndDimensionOptions();
            // Final verification
            setTimeout(() => {
              if (this.targetMetricType && this.selectedMetric !== this.targetMetricType) {
                this.setMetricFromType(this.targetMetricType);
              }
              this.syncMetricAndDimensionOptions();
            }, 100);
          }, 150);
          // Re-sync when dimensions load (async); dimensions may arrive after restore
          setTimeout(() => this.syncMetricAndDimensionOptions(), 400);
          setTimeout(() => this.syncMetricAndDimensionOptions(), 800);
        }, 250);
      } else if (metricsLoaded && criteriaLoaded && this.metrics.length === 0 && metricsRetryCount < maxMetricsRetries) {
        // Metrics loaded but array is empty, wait a bit and retry
        metricsRetryCount++;
        setTimeout(() => {
          checkAndRestore();
        }, 200);
      }
    };

    // Use hardcoded metrics instead of API call
    this.metrics = this.getFallbackMetrics();
    metricsLoaded = true;

    // Update metric options when metrics are loaded
    this.initializeDropdownOptions();

    // If we're editing and have a target metric type, set it now
    if (this.isEditingChart && this.targetMetricType) {
      this.setMetricFromType(this.targetMetricType);
    }

    // Check if we can restore now (in case criteria is already loaded)
    checkAndRestore();

    // Load criteria options
    this.userDashboardService.getCriteriaOptions(chartTypeId).subscribe({
      next: (response: CriteriaOption[] | any) => {
        let criteriaOptionsData: any = null;

        if (response && response.data && response.data.criteria_options) {
          // Standard API response format
          criteriaOptionsData = response.data.criteria_options;
        } else if (response && response.criteria_options) {
          // Alternative format
          criteriaOptionsData = response.criteria_options;
        } else if (Array.isArray(response)) {
          // Direct array response (unlikely but handle it)
          this.criteriaOptions = response;
          this.initializeCriteria(this.criteriaOptions);
          this.ensurePercentageForPieDonutCharts();
          criteriaLoaded = true;
          checkAndRestore();
          return;
        }

        // Convert criteria_options object to CriteriaOption array
        let optionsArray: CriteriaOption[] = [];
        if (criteriaOptionsData && typeof criteriaOptionsData === 'object' && !Array.isArray(criteriaOptionsData)) {
          optionsArray = Object.keys(criteriaOptionsData).map(key => {
            const optionData = criteriaOptionsData[key];
            const apiType = optionData.type?.toLowerCase();
            const formType = this.mapApiTypeToFormType(optionData.type);

            const option: CriteriaOption = {
              field: key,
              label: this.formatLabel(key),
              type: formType,
              min: optionData.min,
              max: optionData.max,
              default: optionData.default,
              options: (apiType === 'enum' && Array.isArray(optionData.options))
                ? optionData.options.map((opt: string) => ({ label: this.formatLabel(opt), value: opt }))
                : undefined
            };
            return option;
          });
        }

        // If no valid criteria found, use fallback
        if (optionsArray.length === 0) {
          // console.warn('No valid criteria found in response, using fallback');
          this.criteriaOptions = this.getFallbackCriteria();
        } else {
          this.criteriaOptions = optionsArray;
        }

        // Initialize criteria values with defaults from API
        this.initializeCriteria(this.criteriaOptions);
        this.ensurePercentageForPieDonutCharts();
        criteriaLoaded = true;
        checkAndRestore();
      },
      error: (error: any) => {
        console.error('Error loading criteria:', error);
        // Use fallback criteria
        this.criteriaOptions = this.getFallbackCriteria();
        this.initializeCriteria(this.criteriaOptions);
        this.ensurePercentageForPieDonutCharts();
        criteriaLoaded = true;
        checkAndRestore();
      }
    });
  }

  /** Filter field options for this form only; no dependency on Insights dashboard or FilterService. */
  private getFallbackFilterFieldOptions(): DropdownObjectType[] {
    return [
      { value: 'queue_name', label: 'Queue Name' },
      { value: 'primarytopic', label: 'Primary Topic' },
      { value: 'intent', label: 'Intent' },
      { value: 'agent_username', label: 'Agent Username' },
      { value: 'customersentimentatstart', label: 'Customer Sentiment At Start' },
      { value: 'customersentimentatend', label: 'Customer Sentiment At End' },
      { value: 'callresolution', label: 'Call Resolution' },
      { value: 'callertype', label: 'Caller Type' },
      { value: 'journeymoment', label: 'Journey Moment' },
      { value: 'TopicsClustered', label: 'Topics Clustered' }
    ];
  }

  /** Display label for a filter field key (used in filter list and applied summary). */
  getFilterFieldLabel(fieldKey: string): string {
    if (!fieldKey) return '';
    const labelMap: { [key: string]: string } = {
      queue_name: 'Queue Name',
      primarytopic: 'Primary Topic',
      intent: 'Intent',
      agent_username: 'Agent Username',
      customersentimentatstart: 'Customer Sentiment At Start',
      customersentimentatend: 'Customer Sentiment At End',
      callresolution: 'Call Resolution',
      callertype: 'Caller Type',
      journeymoment: 'Journey Moment',
      TopicsClustered: 'Topics Clustered',
      RootCauseClustered: 'Root Cause Clustered',
      CustomerPainPointClustered: 'Customer Pain Point Clustered',
      OutcomeClustered: 'Outcome Clustered',
      TriggerClustered: 'Trigger Clustered'
    };
    return labelMap[fieldKey] ?? fieldKey
      .replace(/([A-Z])/g, ' $1')
      .replace(/_/g, ' ')
      .replace(/^\s+/, '')
      .split(' ')
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  /** Display label for filter operator. */
  getFilterOperatorLabel(operator: string): string {
    if (!operator) return '';
    const op = this.filterOperators.find(o => o.value === operator);
    return op?.label ?? operator;
  }

  loadDimensions() {
    this.isLoadingDimensions = true;
    this.userDashboardService.getDimensions().subscribe({
      next: (response: any) => {
        this.isLoadingDimensions = false;
        // Extract dimensions from response
        let dimensionsArray: Dimension[] = [];

        if (response.success && response.data && response.data.dimensions) {
          dimensionsArray = response.data.dimensions;
        } else if (response.data && Array.isArray(response.data)) {
          dimensionsArray = response.data;
        } else if (response.dimensions && Array.isArray(response.dimensions)) {
          dimensionsArray = response.dimensions;
        } else if (Array.isArray(response)) {
          dimensionsArray = response;
        }

        // Normalize dimensions to ensure they have key/value structure
        this.dimensions = dimensionsArray.map((dim: any) => ({
          key: dim.key || dim.name || dim.field || '',
          value: dim.value || dim.display_name || dim.displayName || dim.name || '',
          data_type: dim.data_type || dim.dataType || dim.type || 'string',
          available: dim.available !== undefined ? dim.available : true
        }));

        // Update dimension options when dimensions are loaded
        this.initializeDropdownOptions();
      },
      error: (error) => {
        this.isLoadingDimensions = false;
        console.error('Error loading dimensions from API:', error);
        // Use fallback dimensions if API fails
        this.dimensions = this.getFallbackDimensions();
        // Update dimension options with fallback data
        this.initializeDropdownOptions();
      }
    });
  }

  getFallbackDimensions(): Dimension[] {
    // Fallback dimensions if API fails
    return [
      { key: 'primarytopic', value: 'Primary Topic', data_type: 'string', available: true },
      { key: 'queue_name', value: 'Queue Name', data_type: 'string', available: true },
      { key: 'intent', value: 'Intent', data_type: 'string', available: true },
      { key: 'agent_username', value: 'Agent Username', data_type: 'string', available: true },
      { key: 'rootcauseclustered', value: 'Root Cause', data_type: 'string', available: true },
      { key: 'customerpainpoint', value: 'Customer Pain Point', data_type: 'string', available: true },
      { key: 'outcomeclustered', value: 'Outcome', data_type: 'string', available: true },
      { key: 'triggerclustered', value: 'Trigger', data_type: 'string', available: true },
      { key: 'department', value: 'Department', data_type: 'string', available: false },
      { key: 'location', value: 'Location', data_type: 'string', available: false },
      { key: 'product_category', value: 'Product Category', data_type: 'string', available: false }
    ];
  }

  loadChartTypes() {
    this.isLoading = true;
    this.userDashboardService.getChartTypes().subscribe({
      next: (response: ChartType[] | any) => {
        // Handle different response formats
        let chartTypesArray: any[] = [];

        // Check for the actual API response structure: { data: { chart_types: [...] } }
        if (response && response.data && Array.isArray(response.data.chart_types)) {
          chartTypesArray = response.data.chart_types;
        } else if (Array.isArray(response)) {
          chartTypesArray = response;
        } else if (response && Array.isArray(response.data)) {
          chartTypesArray = response.data;
        } else if (response && Array.isArray(response.chart_types)) {
          chartTypesArray = response.chart_types;
        } else {
          // Fallback: create mock data if API returns empty or unexpected format
          // console.warn('Unexpected API response format, using fallback data');
          chartTypesArray = this.getFallbackChartTypes();
        }

        // Map API response format to ChartType interface
        // API returns { type, name, description, supported_metrics, required_dimensions, availability_status }
        // We need { id, name, description, icon?, availability_status? }
        // Store raw data for later use (supported_metrics)
        const mappedChartTypes: ChartType[] = chartTypesArray.map((chart: any) => {
          const chartType: ChartType = {
            id: chart.type || chart.id, // Use 'type' from API as 'id'
            name: chart.name || '',
            description: chart.description || '',
            icon: chart.icon || this.getIconForChartType(chart.type || chart.id),
            availability_status: chart.availability_status
          };
          // Store raw chart data on the ChartType object for later access
          (chartType as any).rawData = chart;
          return chartType;
        });

        this.chartTypes = mappedChartTypes;
        this.filteredChartTypes = mappedChartTypes;
        this.groupChartTypes();

        // Initialize dropdown options with loaded data
        this.initializeDropdownOptions();

        // If there's a preselected chart type ID, select it now that chart types are loaded
        if (this.preselectedChartTypeId) {
          this.selectChartTypeById(this.preselectedChartTypeId);
        }

        // If there's an existing chart to edit, populate the form now that chart types are loaded
        if (this.existingChart) {
          this.populateFormFromChart(this.existingChart);
        }

        this.isLoading = false;
      },
      error: (error: any) => {
        console.error('Error loading chart types:', error);
        // Use fallback data on error
        this.chartTypes = this.getFallbackChartTypes();
        this.filteredChartTypes = this.chartTypes;
        this.groupChartTypes();
        // Initialize dropdown options with fallback data
        this.initializeDropdownOptions();
        this.isLoading = false;
      }
    });
  }

  getIconForChartType(chartType: string): string {
    const base = 'assets/icons/user-dashbaord';
    const iconMap: { [key: string]: string } = {
      'bar': `${base}/chart1.png`,
      'column': `${base}/chart2.png`,
      'pie': `${base}/chart-pie.png`,
      'donut': `${base}/chart-donut.png`,
      'line': `${base}/chart-line.png`,
      'spline': `${base}/chart-spline.png`,
      'area': `${base}/chart-area.png`,
      'areaspline': `${base}/chart-areaspline.png`,
      'area_spline': `${base}/chart-areaspline.png`,
      'scatter': `${base}/chart-scatter.png`,
      'gauge': `${base}/chart-gauge.png`,
      'heatmap': `${base}/chart-heatmap.png`,
      'treemap': `${base}/chart-treemap.png`,
      'sankey': `${base}/chart-sankey.png`,
      'bubble': `${base}/chart-bubble.png`,
      'waterfall': `${base}/chart-waterfall.png`,
      'polar': `${base}/chart-radar.png`,
      'radar': `${base}/chart-radar.png`
    };
    return iconMap[chartType?.toLowerCase()] || 'assets/icons/bar_chart_icon.svg';
  }

  getFallbackChartTypes(): ChartType[] {
    const base = 'assets/icons/user-dashbaord';
    return [
      { id: 'bar', name: 'Basic Bar Chart', description: 'Use this chart when you need to visualise two data points.', icon: `${base}/chart1.png` },
      { id: 'column', name: 'Basic Column', description: 'Use this chart when you need to visualise two data points.', icon: `${base}/chart2.png` },
      { id: 'pie', name: 'Pie Chart', description: 'Use this chart to show proportions.', icon: `${base}/chart-pie.png` },
      { id: 'donut', name: 'Donut Chart', description: 'Use this chart to show proportions with a hole in the center.', icon: `${base}/chart-donut.png` },
      { id: 'line', name: 'Line Chart', description: 'Use this chart to show trends over time.', icon: `${base}/chart-line.png` },
      { id: 'spline', name: 'Spline Chart', description: 'Use this chart to show smooth trends over time.', icon: `${base}/chart-spline.png` },
      { id: 'area', name: 'Area Chart', description: 'Use this chart to show cumulative values over time.', icon: `${base}/chart-area.png` },
      { id: 'areaspline', name: 'Area Spline Chart', description: 'Use this chart to show cumulative values with smooth curves.', icon: `${base}/chart-areaspline.png` },
      { id: 'scatter', name: 'Scatter Plot', description: 'Use this chart to show correlation between two variables.', icon: `${base}/chart-scatter.png` },
      { id: 'gauge', name: 'Gauge Chart', description: 'Use this chart to show a single value within a range.', icon: `${base}/chart-gauge.png` },
      { id: 'heatmap', name: 'Heatmap', description: 'Use this chart to show density or intensity across two dimensions.', icon: `${base}/chart-heatmap.png` },
      { id: 'treemap', name: 'Treemap', description: 'Use this chart to show hierarchical data as nested rectangles.', icon: `${base}/chart-treemap.png` },
      { id: 'sankey', name: 'Sankey Chart', description: 'Use this chart to show flow between categories.', icon: `${base}/chart-sankey.png` },
      { id: 'bubble', name: 'Bubble Chart', description: 'Use this chart to show three dimensions with bubble size.', icon: `${base}/chart-bubble.png` },
      { id: 'waterfall', name: 'Waterfall Chart', description: 'Use this chart to show cumulative effect of sequential values.', icon: `${base}/chart-waterfall.png` },
      { id: 'polar', name: 'Polar/Radar Chart', description: 'Use this chart to show multi-variable data on axes from a point.', icon: `${base}/chart-radar.png` },
    ];
  }

  groupChartTypes() {
    // Group chart types by category - use filteredChartTypes to respect search filtering
    // If there's a search query, always use filteredChartTypes (even if empty)
    // Otherwise, use filteredChartTypes if it has items, or fall back to chartTypes
    const chartTypesToGroup = this.searchQuery.trim()
      ? this.filteredChartTypes
      : (this.filteredChartTypes.length > 0 ? this.filteredChartTypes : this.chartTypes);

    const barColumn = chartTypesToGroup.filter(t =>
      t.name.toLowerCase().includes('bar') ||
      t.name.toLowerCase().includes('column') ||
      t.id.toLowerCase().includes('bar') ||
      t.id.toLowerCase().includes('column')
    );

    const line = chartTypesToGroup.filter(t =>
      (t.name.toLowerCase().includes('line') || t.name.toLowerCase().includes('spline')) ||
      (t.id.toLowerCase().includes('line') || t.id.toLowerCase().includes('spline'))
    );

    const pieDonut = chartTypesToGroup.filter(t =>
      t.name.toLowerCase().includes('pie') ||
      t.name.toLowerCase().includes('donut') ||
      t.id.toLowerCase().includes('pie') ||
      t.id.toLowerCase().includes('donut')
    );

    const area = chartTypesToGroup.filter(t =>
      (t.name.toLowerCase().includes('area') || t.name.toLowerCase().includes('areaspline')) ||
      (t.id.toLowerCase().includes('area') || t.id.toLowerCase().includes('areaspline'))
    );

    const special = chartTypesToGroup.filter(t =>
      t.id.toLowerCase().includes('scatter') ||
      t.id.toLowerCase().includes('gauge') ||
      t.id.toLowerCase().includes('heatmap') ||
      t.id.toLowerCase().includes('treemap') ||
      t.id.toLowerCase().includes('sankey') ||
      t.id.toLowerCase().includes('bubble') ||
      t.id.toLowerCase().includes('waterfall') ||
      t.id.toLowerCase().includes('polar')
    );

    // Get remaining chart types that don't fit into categories
    const categorizedIds = new Set([
      ...barColumn.map(t => t.id),
      ...line.map(t => t.id),
      ...pieDonut.map(t => t.id),
      ...area.map(t => t.id),
      ...special.map(t => t.id)
    ]);
    const other = chartTypesToGroup.filter(t => !categorizedIds.has(t.id));

    // Only add categories that have items - reordered by priority
    this.chartCategories = {};

    // Priority 1: Column & Bar Charts
    if (barColumn.length > 0) {
      this.chartCategories['Column & Bar Chart'] = barColumn;
    }

    // Priority 2: Pie & Donut Charts
    if (pieDonut.length > 0) {
      this.chartCategories['Pie & Donut Chart'] = pieDonut;
    }

    // Priority 3: Line Charts
    if (line.length > 0) {
      this.chartCategories['Line Chart'] = line;
    }

    // Priority 4: Area Charts
    if (area.length > 0) {
      this.chartCategories['Area Chart'] = area;
    }

    // Priority 5: Special Charts
    if (special.length > 0) {
      this.chartCategories['Special Charts'] = special;
    }

    // Priority 6: Other Charts (everything else)
    if (other.length > 0) {
      this.chartCategories['Other Charts'] = other;
    }

    // If no categories match, put all in "All Charts"
    if (Object.keys(this.chartCategories).length === 0 && chartTypesToGroup.length > 0) {
      this.chartCategories['All Charts'] = chartTypesToGroup;
    }
  }

  onSearch() {
    if (!this.searchQuery.trim()) {
      this.filteredChartTypes = [];
      this.groupChartTypes();
      return;
    }
    this.filteredChartTypes = this.chartTypes.filter(type =>
      type.name.toLowerCase().includes(this.searchQuery.toLowerCase())
    );
    this.groupChartTypes();
  }

  // Helper method to get only available chart types for dropdown
  getAvailableChartTypes(): ChartType[] {
    return this.chartTypes.filter(chartType => chartType.availability_status === true);
  }

  onChartTypeSelect(chartType: ChartType) {
    this.selectedChartType = chartType;
    this.selectedChartTypeId = chartType.id;
    // Store raw chart data if available
    this.selectedChartTypeRaw = (chartType as any).rawData || null;

    // Set the chart type dropdown option
    this.selectedChartTypeOption = this.chartTypeOptions.find(option => option.value === chartType.id) || null;

    // Reset form when new chart type is selected
    this.chartTitle = '';
    this.selectedMetric = '';

    // Reset dimensions based on new chart type requirements
    const requiredDimensions = this.selectedChartTypeRaw?.required_dimensions || 0;
    const chartTypeId = chartType.id.toLowerCase();
    const chartsWithoutDimensions = ['gauge'];

    // Reset dimensions if chart type doesn't need them
    if (chartsWithoutDimensions.includes(chartTypeId) && requiredDimensions === 0) {
      this.selectedDimension = '';
      this.selectedSecondaryDimension = null;
    } else if (requiredDimensions === 0) {
      // Optional dimensions - keep existing or reset
      // Don't reset if user already selected something
    } else {
      // Reset if requirements changed
      this.selectedDimension = '';
      this.selectedSecondaryDimension = null;
    }

    this.selectedFilterField = '';
    this.selectedFilterValue = '';
    this.selectedFilterOperator = '';

    // Always load chart details when chart type is selected
    this.loadChartDetails(chartType.id);

    if (this.showTemplatesOnly) {
      this.chartSelected.emit(chartType.id);
    }
  }

  // New method for form-select-dropdown component
  onChartTypeDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedChartTypeOption = selectedOption;
    const chartType = this.chartTypes.find(ct => ct.id === selectedOption.value);
    if (chartType) {
      this.onChartTypeSelect(chartType);
    }
  }

  // New method for metric dropdown selection
  onMetricDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedMetricOption = selectedOption;
    this.selectedMetric = selectedOption.value;
  }

  // New method for date range dropdown selection
  onDateRangeDropdownSelect(selectedOption: DropdownObjectType) {
    //console.log('DEBUG: onDateRangeDropdownSelect called - isPopulatingForm:', this.isPopulatingForm, 'selectedOption:', selectedOption);
    //console.log('DEBUG: Current dates before dropdown select - startDate:', this.startDate, 'endDate:', this.endDate);

    // Don't trigger change during form population
    if (this.isPopulatingForm) {
      //console.log('DEBUG: Setting dropdown without triggering change due to isPopulatingForm=true');
      this.selectedDateRangeOption = selectedOption;
      this.dateRangeType = selectedOption.value as any;
      return;
    }

    this.selectedDateRangeOption = selectedOption;
    this.dateRangeType = selectedOption.value as any;
    //console.log('DEBUG: onDateRangeDropdownSelect - about to call onDateRangeTypeChange');
    this.onDateRangeTypeChange();
    //console.log('DEBUG: onDateRangeDropdownSelect - after onDateRangeTypeChange');
  }

  onTimezoneDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedTimezoneOption = selectedOption;
    this.selectedTimezone = selectedOption.value ?? 'EST';
  }

  // New method for dimension dropdown selection
  onDimensionDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedDimensionOption = selectedOption;
    this.selectedDimension = selectedOption.value;
  }

  // New method for secondary dimension dropdown selection
  onSecondaryDimensionDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedSecondaryDimensionOption = selectedOption;
    this.selectedSecondaryDimension = selectedOption.value;
  }

  // Get secondary dimension options with None option
  getSecondaryDimensionOptions(): DropdownObjectType[] {
    const options = [
      { value: '', label: 'None' },
      ...this.dimensionOptions
    ];

    // Set selected secondary dimension option if not already set
    if (this.selectedSecondaryDimension !== undefined && !this.selectedSecondaryDimensionOption) {
      this.selectedSecondaryDimensionOption = options.find(option => option.value === this.selectedSecondaryDimension) || null;
    }

    return options;
  }

  // New method for filter field dropdown selection
  onFilterFieldDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedFilterFieldOption = selectedOption;
    this.selectedFilterField = selectedOption.value;
    // Reset operator and value when field changes
    this.selectedFilterOperatorOption = null;
    this.selectedFilterOperator = '';
    this.selectedFilterValue = '';
  }

  // New method for filter operator dropdown selection
  onFilterOperatorDropdownSelect(selectedOption: DropdownObjectType) {
    this.selectedFilterOperatorOption = selectedOption;
    this.selectedFilterOperator = selectedOption.value;
  }

  // Initialize dropdown options
  initializeDropdownOptions() {
    // Chart Type options
    this.chartTypeOptions = this.getAvailableChartTypes().map(chartType => ({
      value: chartType.id,
      label: chartType.name
    }));

    // Metric options - filter based on chart type
    let availableMetrics = this.metrics;
    if (this.isPieOrDonutChart()) {
      // For pie and donut charts, only show percentage metrics
      availableMetrics = this.metrics.filter(metric => metric.id === 'percentage');

      // Auto-select percentage metric if current metric is volume
      if (this.selectedMetric === 'volume') {
        this.selectedMetric = 'percentage';
        this.selectedMetricOption = { value: 'percentage', label: 'Percentage' };
      }
    }

    this.metricOptions = availableMetrics.map(metric => ({
      value: metric.id,
      label: metric.name || metric.id
    }));

    // Dimension options - include all dimensions with availability status
    this.dimensionOptions = this.dimensions.map(dim => ({
      value: dim.key,
      label: dim.value,
      status: dim.available ? 'available' : 'unavailable'
    }));

    // Set selected dimension option if not already set (match by value/key or label for API flexibility)
    if (this.selectedDimension) {
      const dimOpt = this.dimensionOptions.find(opt =>
        opt.value === this.selectedDimension ||
        (opt as any).label?.toLowerCase() === this.selectedDimension?.toLowerCase()
      ) || null;
      if (dimOpt) this.selectedDimensionOption = dimOpt;
    }

    // Date Range options
    this.dateRangeOptions = [
      { value: 'today', label: 'Today (Regular update)' },
      { value: 'this_week', label: 'This Week (Regular update)' },
      { value: 'this_month', label: 'This Month (Regular update)' },
      { value: 'this_quarter', label: 'This Quarter (Regular update)' },
      // { value: 'this_year', label: 'This Year (Regular update)' },
      { value: 'last_n_days', label: 'Last N Days (Regular update)' },
      { value: 'custom_range', label: 'Custom Range (Fixed)' }
    ];

    // Set selected date range option if not already set
    if (this.dateRangeType && !this.selectedDateRangeOption) {
      this.selectedDateRangeOption = this.dateRangeOptions.find(option => option.value === this.dateRangeType) || null;
    }

    // Time zone options (same list as Insights dashboard)
    this.timezoneOptions = [...TIMEZONE_LIST];
    if (!this.selectedTimezoneOption || !this.timezoneOptions.some(o => o.value === this.selectedTimezone)) {
      this.selectedTimezoneOption = this.timezoneOptions.find(o => o.value === this.selectedTimezone) ?? this.timezoneOptions.find(o => o.value === 'EST') ?? this.timezoneOptions[0] ?? null;
    }

    // Filter Field options (local list; no dependency on Insights dashboard)
    this.filterFieldOptions = this.getFallbackFilterFieldOptions();

    // Filter Operator options
    this.filterOperatorOptions = this.filterOperators.map(op => ({
      value: op.value,
      label: op.label
    }));
  }

  /** Sync selected option objects from current metric/dimension values (e.g. after restore when editing). */
  syncMetricAndDimensionOptions() {
    if (this.selectedMetric && this.metricOptions.length > 0) {
      const opt = this.metricOptions.find(o => o.value === this.selectedMetric || (o as any).label?.toLowerCase() === this.selectedMetric?.toLowerCase());
      if (opt) this.selectedMetricOption = opt;
    }
    if (this.selectedDimension && this.dimensionOptions.length > 0) {
      const opt = this.dimensionOptions.find(o => o.value === this.selectedDimension || (o as any).label?.toLowerCase() === this.selectedDimension?.toLowerCase());
      if (opt) this.selectedDimensionOption = opt;
    }
    if (this.selectedSecondaryDimension && this.dimensionOptions.length > 0) {
      const secondaryOpts = this.getSecondaryDimensionOptions();
      const opt = secondaryOpts.find(o => o.value === this.selectedSecondaryDimension || (o as any).label?.toLowerCase() === this.selectedSecondaryDimension?.toLowerCase());
      if (opt) this.selectedSecondaryDimensionOption = opt;
    }
  }

  onChartTypeDropdownChange(chartTypeId: string) {
    this.selectedChartTypeId = chartTypeId || '';

    if (!chartTypeId) {
      this.selectedChartType = null;
      this.selectedChartTypeRaw = null;
      this.metrics = [];
      this.criteriaOptions = [];
      this.selectedMetric = '';
      this.selectedDimension = '';
      this.selectedSecondaryDimension = null;
      return;
    }

    // If we're editing, don't call onChartTypeSelect which resets the form
    // The form values will be restored by the callback
    if (this.isEditingChart) {
      const chartType = this.chartTypes.find(ct => ct.id === chartTypeId);
      if (chartType) {
        // Just set the chart type without resetting form
        this.selectedChartType = chartType;
        this.selectedChartTypeRaw = (chartType as any).rawData || null;
      }
      return;
    }

    const chartType = this.chartTypes.find(ct => ct.id === chartTypeId);
    if (chartType) {
      this.onChartTypeSelect(chartType);
    }
  }

  loadChartDetails(chartTypeId: string) {
    this.isLoading = true;
    this.metrics = [];
    this.criteriaOptions = [];
    // Don't reset selectedMetric if we're editing and have a target metric type
    if (!this.isEditingChart || !this.targetMetricType) {
      this.selectedMetric = '';
    }

    // Use hardcoded metrics instead of API call
    this.metrics = this.getFallbackMetrics();
    if (this.metrics.length > 0 && (!this.isEditingChart || !this.targetMetricType)) {
      // Default to volume when available (e.g. for bar/line); pie/donut only have percentage
      const defaultMetric = this.metrics.find(m => m.id === 'volume') ?? this.metrics[0];
      this.selectedMetric = defaultMetric.id;
    }

    // Update metric options when metrics are loaded
    this.initializeDropdownOptions();

    // Load criteria options
    this.userDashboardService.getCriteriaOptions(chartTypeId).subscribe({
      next: (response: CriteriaOption[] | any) => {
        let criteriaOptionsData: any = null;

        if (response && response.data && response.data.criteria_options) {
          // Standard API response format
          criteriaOptionsData = response.data.criteria_options;
        } else if (response && response.criteria_options) {
          // Alternative format
          criteriaOptionsData = response.criteria_options;
        } else if (Array.isArray(response)) {
          // Direct array response (unlikely but handle it)
          this.criteriaOptions = response;
          this.initializeCriteria(this.criteriaOptions);
          this.checkLoadingComplete();
          return;
        }

        // Convert criteria_options object to CriteriaOption array
        let optionsArray: CriteriaOption[] = [];
        if (criteriaOptionsData && typeof criteriaOptionsData === 'object' && !Array.isArray(criteriaOptionsData)) {
          optionsArray = Object.keys(criteriaOptionsData).map(key => {
            const optionData = criteriaOptionsData[key];
            const apiType = optionData.type?.toLowerCase();
            const formType = this.mapApiTypeToFormType(optionData.type);

            const option: CriteriaOption = {
              field: key,
              label: this.formatLabel(key),
              type: formType,
              min: optionData.min,
              max: optionData.max,
              default: optionData.default,
              options: (apiType === 'enum' && Array.isArray(optionData.options))
                ? optionData.options.map((opt: string) => ({ label: this.formatLabel(opt), value: opt }))
                : undefined
            };
            return option;
          });
        }

        // If no valid criteria found, use fallback
        if (optionsArray.length === 0) {
          // console.warn('No valid criteria found in response, using fallback');
          this.criteriaOptions = this.getFallbackCriteria();
        } else {
          this.criteriaOptions = optionsArray;
        }

        // Initialize criteria values with defaults from API
        this.initializeCriteria(this.criteriaOptions);
        this.checkLoadingComplete();
      },
      error: (error: any) => {
        console.error('Error loading criteria:', error);
        // Use fallback criteria
        this.criteriaOptions = this.getFallbackCriteria();
        this.initializeCriteria(this.criteriaOptions);
        this.checkLoadingComplete();
      }
    });
  }

  private checkLoadingComplete() {
    // Check if both metrics and criteria have been loaded (or failed)
    // This is a simple approach - you might want to use a more robust loading state
    if (this.metrics.length >= 0 && this.criteriaOptions.length >= 0) {
      this.isLoading = false;
      this.ensurePercentageForPieDonutCharts();
    }
  }

  private formatLabel(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private mapApiTypeToFormType(apiType: string): 'number' | 'string' | 'select' | 'boolean' {
    // Map API types to form input types
    switch (apiType?.toLowerCase()) {
      case 'integer':
      case 'number':
      case 'int':
        return 'number';
      case 'enum':
      case 'select':
        return 'select';
      case 'string':
      case 'text':
        return 'string';
      case 'boolean':
      case 'bool':
        return 'boolean';
      default:
        return 'string';
    }
  }

  private determineType(value: any): 'number' | 'string' | 'select' {
    // Skip null, undefined, and objects (which are likely API response wrappers)
    if (value === null || value === undefined || (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length > 5)) {
      return 'string'; // Default, but these should be filtered out
    }
    if (Array.isArray(value)) {
      return 'select';
    }
    if (typeof value === 'number') {
      return 'number';
    }
    if (typeof value === 'string') {
      // Check if it's a known select value
      const selectValues = ['day', 'week', 'month', 'hour', 'desc', 'asc', 'descending', 'ascending'];
      if (selectValues.includes(value.toLowerCase())) {
        return 'select';
      }
      // If it's a long string or looks like JSON/object string, skip it
      if (value.length > 50 || value.startsWith('{') || value.startsWith('[')) {
        return 'string'; // But should be filtered
      }
    }
    return 'string';
  }

  private getSelectOptions(key: string, value: any): any[] {
    if (key === 'time_bucket') {
      return [
        { label: 'Hour', value: 'hour' },
        { label: 'Day', value: 'day' },
        { label: 'Week', value: 'week' },
        { label: 'Month', value: 'month' }
      ];
    }
    if (key === 'sort_order') {
      return [
        { label: 'Descending', value: 'desc' },
        { label: 'Ascending', value: 'asc' }
      ];
    }
    if (Array.isArray(value)) {
      return value.map(v => ({ label: String(v), value: v }));
    }
    return [];
  }

  private initializeCriteria(options: CriteriaOption[]) {
    // Filter out any invalid options (API response fields)
    const excludedFields = ['id', 'object', 'created', 'data', 'success', 'message', 'detail'];
    const validOptions = options.filter(opt => !excludedFields.includes(opt.field.toLowerCase()));

    validOptions.forEach(option => {
      // Skip if already initialized or if it's an excluded field
      if (excludedFields.includes(option.field.toLowerCase())) {
        return;
      }
      // Use default value from API if available, otherwise use field-specific defaults
      if (!this.criteria.hasOwnProperty(option.field)) {
        if (option.default !== undefined && option.default !== null) {
          // Use API-provided default
          this.criteria[option.field] = option.default;
        } else if (option.type === 'number') {
          // Use min value or 0 as default for numbers
          this.criteria[option.field] = option.min !== undefined ? option.min : (option.field === 'top_n' ? 10 : 0);
        } else if (option.type === 'select' && option.options && option.options.length > 0) {
          // Use first option as default
          this.criteria[option.field] = option.options[0].value;
        } else if (option.type === 'boolean') {
          // show_percentage (Show Labels) enabled by default; others default to false
          this.criteria[option.field] = option.field === 'show_percentage' ? true : false;
        } else if (option.type === 'string') {
          // Only initialize string fields if they have a reasonable default
          if (option.field === 'time_bucket') {
            this.criteria[option.field] = 'day';
          } else if (option.field === 'sort_order') {
            this.criteria[option.field] = 'desc';
          }
          // Don't initialize other string fields
        }
      }
    });
  }

  formatMetricName(metricId: string): string {
    const metricNames: { [key: string]: string } = {
      'volume': 'Volume',
      'count': 'Count',
      'sentiment_score': 'Sentiment Score',
      'trend': 'Trend',
      'cumulative': 'Cumulative',
      'percentage': 'Percentage',
      'top_n': 'Top N',
      'resolution_rate': 'Resolution Rate',
      'growth_rate': 'Growth Rate'
    };
    return metricNames[metricId] || metricId.charAt(0).toUpperCase() + metricId.slice(1).replace(/_/g, ' ');
  }

  // Map display names to database column names
  mapFieldNameToDatabaseColumn(fieldName: string): string {
    const fieldMapping: { [key: string]: string } = {
      'QueueName': 'queue_name',
      'Queue_Name': 'queue_name',
      'PrimaryTopic': 'primarytopic',
      'Intent': 'intent',
      'Agent_Username': 'agent_username',
      'RootCauseClustered': 'rootcauseclustered',
      'CustomerPainPointClustered': 'customerpainpointclustered',
      'OutcomeClustered': 'outcomeclustered',
      'TriggerClustered': 'triggerclustered',
      'CallerType': 'callertype',
      'CallResolution': 'callresolution',
      'CustomerSentimentAtStart': 'customersentimentatstart',
      'CustomerSentimentAtEnd': 'customersentimentatend',
      'JourneyMoment': 'journeymoment'
    };

    // Return mapped name if exists, otherwise return lowercase with underscores
    if (fieldMapping[fieldName]) {
      return fieldMapping[fieldName];
    }

    // Fallback: convert to lowercase and replace spaces/camelCase with underscores
    return fieldName
      .replace(/([A-Z])/g, '_$1')
      .toLowerCase()
      .replace(/^_/, '')
      .replace(/\s+/g, '_');
  }

  getFallbackMetrics(): Metric[] {
    const allMetrics = [
      { id: 'volume', name: 'Volume', description: 'Number of conversations' },
      { id: 'percentage', name: 'Percentage', description: 'Percentage value' }
    ];

    // For pie and donut charts, only show percentage metrics
    if (this.isPieOrDonutChart()) {
      return allMetrics.filter(metric => metric.id === 'percentage');
    }

    return allMetrics;
  }

  getFallbackCriteria(): CriteriaOption[] {
    return [
      { field: 'top_n', label: 'Top N', type: 'number' },
      { field: 'time_bucket', label: 'Time Bucket', type: 'select', options: [
        { label: 'Hour', value: 'hour' },
        { label: 'Day', value: 'day' },
        { label: 'Week', value: 'week' },
        { label: 'Month', value: 'month' }
      ]},
      { field: 'sort_order', label: 'Sort Order', type: 'select', options: [
        { label: 'Descending', value: 'desc' },
        { label: 'Ascending', value: 'asc' }
      ]}
    ];
  }

  selectChartTypeById(chartTypeId: string) {
    // Find and select the chart type by ID
    const chartType = this.chartTypes.find(ct => ct.id === chartTypeId);
    if (chartType) {
      this.onChartTypeSelect(chartType);
      // Ensure the dropdown reflects the selection
      this.selectedChartTypeId = chartType.id;
    }
  }

  onAddChartTemplate(chartType: ChartType) {
    // Select the chart type - this will set selectedChartTypeId which updates the dropdown
    this.onChartTypeSelect(chartType);

    // Ensure the dropdown reflects the selection
    this.selectedChartTypeId = chartType.id;

    if (this.showTemplatesOnly) {
      // Load chart details before emitting
      this.loadChartDetails(chartType.id);
      // Emit event to parent to show form - this is when template is selected
      this.chartSelected.emit(chartType.id);
    } else {
      // If not in templates-only mode, load details directly
      this.loadChartDetails(chartType.id);
    }
  }

  onSaveChart() {
    // Validation
    if (!this.selectedChartType) {
      this.notificationService.error('Validation Error', 'Please select a chart type');
      return;
    }

    if (!this.selectedMetric) {
      this.notificationService.error('Validation Error', 'Please select a metric type');
      return;
    }

    // Check required dimensions based on chart type
    const requiredDimensions = this.selectedChartTypeRaw?.required_dimensions || 0;
    const chartTypeId = this.selectedChartType.id.toLowerCase();

    // Charts that don't require dimensions (like gauge)
    const chartsWithoutDimensions = ['gauge'];

    // Validate primary dimension
    if (requiredDimensions > 0 && !this.selectedDimension) {
      this.notificationService.error('Validation Error', 'This chart type requires a dimension. Please select a dimension.');
      return;
    }

    // For charts that typically need dimensions but don't have required_dimensions set
    if (requiredDimensions === 0 && !chartsWithoutDimensions.includes(chartTypeId)) {
      // Warn but don't block - dimension is recommended but not required
      const chartTypesNeedingDimension = ['bar', 'column', 'line', 'area', 'pie', 'scatter', 'heatmap', 'treemap', 'sankey', 'bubble', 'waterfall', 'polar'];
      if (chartTypesNeedingDimension.includes(chartTypeId) && !this.selectedDimension) {
        // Just a warning, not blocking
        // console.warn('Chart type typically requires a dimension for proper visualization');
      }
    }

    // Validate secondary dimension
    if (requiredDimensions === 2) {
      if (!this.selectedSecondaryDimension || this.selectedSecondaryDimension === '' || this.selectedSecondaryDimension === null) {
        this.notificationService.error('Validation Error', 'This chart type requires a secondary dimension. Please select a secondary dimension.');
        return;
      }

      // Ensure secondary dimension is different from primary
      if (this.selectedSecondaryDimension === this.selectedDimension) {
        this.notificationService.error('Validation Error', 'Secondary dimension must be different from primary dimension.');
        return;
      }
    }

    // Validate date range - either custom_range dates or dynamic date range
    if (this.dateRangeType && this.dateRangeType !== 'custom_range') {
      // Dynamic date range selected
      if (this.dateRangeType === 'last_n_days') {
        if (!this.lastNDays || this.lastNDays < 1) {
          this.notificationService.error('Validation Error', 'Please enter a valid number of days (must be at least 1).');
          return;
        }
      }
      // Other dynamic ranges (today, this_week, this_month, this_quarter, this_year, last_n_days) don't need additional validation
    } else {
      // Custom date range selected
      if (!this.startDate || !this.endDate) {
        this.notificationService.error('Validation Error', 'Please select a valid date range.');
        return;
      }

      const rangeCheck = checkCustomDateRange(this.startDate, this.endDate);
      if (!rangeCheck.valid) {
        this.notificationService.error('Validation Error', rangeCheck.error);
        return;
      }
    }

    if (this.isPieOrDonutChart()) {
      const raw = this.criteria.min_slice_percentage;
      if (raw === null || raw === undefined || (typeof raw === 'string' && String(raw).trim() === '')) {
        this.notificationService.error(
          'Validation Error',
          'Minimum slice percentage is required. Enter a number from 0 to 10.'
        );
        return;
      }
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isNaN(n) || n < 0 || n > 10) {
        this.notificationService.error(
          'Validation Error',
          'Minimum slice percentage must be between 0 and 10 (no negative values).'
        );
        return;
      }
      this.criteria.min_slice_percentage = n;
    }

    // Build filters array — same payload shape as Insights dashboard (FilterRequest[])
    // Prefer live applied state from the wrapper; fall back to initial conditions if wrapper is unavailable
    const filters: Types.FilterRequest[] = this.chartFilterWrapper
      ? this.chartFilterWrapper.getFiltersForPayload()
      : transformTestToFilters(this.initialFilterConditions ?? []).data;

    // Build criteria object - only include defined values, exclude API response fields
    const excludedCriteriaFields = ['id', 'object', 'created', 'data', 'success', 'message', 'detail', 'categories', 'response'];
    const criteria: any = {};

    // Process all criteria fields, excluding API response wrapper fields
    Object.keys(this.criteria).forEach(key => {
      // Skip excluded fields and empty values
      if (excludedCriteriaFields.includes(key.toLowerCase())) {
        return;
      }

      if (key === 'top_n') {
        const topN = this.criteria[key];
        if (topN === null || topN === undefined || (typeof topN === 'string' && topN.trim() === '')) {
          criteria.top_n = null;
        } else if (typeof topN === 'string' && !isNaN(Number(topN))) {
          const numValue = Math.floor(Number(topN));
          criteria.top_n = numValue >= 1 ? numValue : 1;
        } else if (typeof topN === 'number') {
          criteria.top_n = topN >= 1 ? Math.floor(topN) : 1;
        }
        return;
      }

      const value = this.criteria[key];
      if (value !== undefined && value !== null && value !== '') {
        // Convert numbers properly
        if (typeof value === 'number') {
          criteria[key] = value;
        } else if (typeof value === 'boolean') {
          criteria[key] = value;
        } else if (typeof value === 'string' && value.trim() !== '') {
          criteria[key] = value.trim();
        } else {
          criteria[key] = value;
        }
      }
    });

    // Build request according to Postman collection format exactly:
    // { chart_type, metric_type, start_date, end_date, dimension, secondary_dimension, filters, criteria, chart_title, chart_subtitle }
    const request: any = {
      chart_type: this.selectedChartType.id,
      metric_type: this.selectedMetric
    };

    // Handle date range - either custom_range dates or dynamic date range
    if (this.dateRangeType && this.dateRangeType !== 'custom_range') {
      // Dynamic date range - don't include start_date/end_date, include date_range_type in criteria
      if (!criteria.date_range_type) {
        criteria.date_range_type = this.dateRangeType;
      }

      // Only include last_n_days if date_range_type is 'last_n_days'
      if (this.dateRangeType === 'last_n_days' && this.lastNDays) {
        criteria.last_n_days = this.lastNDays;
      } else {
        // Remove last_n_days from criteria if date_range_type is not 'last_n_days'
        delete criteria.last_n_days;
      }
    } else {
      // Custom date range - include start_date and end_date (date only, no timestamp)
      request.start_date = dayjs(this.startDate).format('YYYY-MM-DD');
      request.end_date = dayjs(this.endDate).format('YYYY-MM-DD');
      // Remove date range related fields from criteria for custom_range
      delete criteria.date_range_type;
      delete criteria.last_n_days;
    }

    // Dimension handling based on chart type requirements
    // Reuse chartTypeId and chartsWithoutDimensions from validation above

    // For charts that don't need dimensions, don't include dimension field
    if (chartsWithoutDimensions.includes(chartTypeId) && requiredDimensions === 0) {
      // Gauge and similar charts don't need dimensions
      // Don't include dimension in request
    } else if (this.selectedDimension && this.selectedDimension.trim() !== '') {
      // Include dimension if provided - use name directly from API (already in correct format)
      request.dimension = this.selectedDimension.trim();
    } else if (requiredDimensions > 0) {
      // If dimension is required but not provided, this should have been caught in validation
      // But add as safety check
      this.notificationService.error('Validation Error', 'Dimension is required for this chart type. Please select a dimension.');
      return;
    }
    // For optional dimensions, don't include if not provided

    // Add secondary_dimension - can be null
    if (this.selectedSecondaryDimension !== null && this.selectedSecondaryDimension !== undefined && this.selectedSecondaryDimension !== '' && this.selectedSecondaryDimension.trim() !== '') {
      // Use name directly from API (already in correct format)
      request.secondary_dimension = this.selectedSecondaryDimension.trim();
    } else {
      // Always include secondary_dimension, set to null if not provided
      request.secondary_dimension = null;
    }

    // Filters — same shape as Insights dashboard (FilterRequest[]); always include (may be empty)
    request.filters = filters;

    // Add criteria only if we have any (don't send empty object)
    if (Object.keys(criteria).length > 0) {
      request.criteria = criteria;
    }

    // Add chart_title - null if empty
    request.chart_title = (this.chartTitle && this.chartTitle.trim() !== '') ? this.chartTitle.trim() : null;

    // Always include chart_subtitle as null (required by API)
    request.chart_subtitle = null;

    // Add x_axis_name and y_axis_name if available (from API docs)
    // Preserve from existing chart request if editing, otherwise null
    request.x_axis_name = (this.existingChartRequest?.x_axis_name && this.existingChartRequest.x_axis_name.trim() !== '')
      ? this.existingChartRequest.x_axis_name.trim()
      : null;
    request.y_axis_name = (this.existingChartRequest?.y_axis_name && this.existingChartRequest.y_axis_name.trim() !== '')
      ? this.existingChartRequest.y_axis_name.trim()
      : null;

    // Time zone (default EST per Insights dashboard)
    request.time_zone = this.selectedTimezone?.trim() || 'EST';

    // Final cleanup: Remove any fields that shouldn't be in the request
    // Only keep fields that are in the Postman collection payload
    const allowedFields = [
      'chart_type',
      'metric_type',
      'start_date',
      'end_date',
      'dimension',
      'secondary_dimension',
      'filters',
      'criteria',
      'chart_title',
      'chart_subtitle',
      'x_axis_name',
      'y_axis_name',
      'time_zone'
    ];

    const cleanedRequest: any = {};
    allowedFields.forEach(field => {
      if (request[field] !== undefined) {
        cleanedRequest[field] = request[field];
      }
    });

    this.isLoading = true;
    this.isSavingChart = true;
    // Show processing notification
    const processingNotification = this.notificationService.processing(
      this.currentChartId ? 'Updating Chart' : 'Generating Chart',
      this.currentChartId ? 'Updating Chart' : 'Generate chart',
      0
    );

    // Use updateChart if editing, otherwise use generateChart
    const chartObservable = this.currentChartId
      ? this.userDashboardService.updateChart(this.currentChartId, cleanedRequest)
      : this.userDashboardService.generateChart(cleanedRequest);

    chartObservable.subscribe({
      next: (response: any) => {
        // Dismiss processing notification
        processingNotification.dismiss();

        // Handle both API response formats: /generate (response.data = chart) and dashboard (response.data.Charts[])
        const chartDataResponse = this.userDashboardService.extractChartData(response);

        // API returns PascalCase format: ChartId, ChartType, ChartConfig, Series, Categories, TableData, Title, NoDataFound, etc.

        // Extract chart_id from response (API uses PascalCase: ChartId)
        // For updates, preserve currentChartId if API doesn't return ChartId
        let chartId: string | undefined;
        if (chartDataResponse?.ChartId) {
          chartId = chartDataResponse.ChartId;
        } else if (response?.data?.ChartId) {
          chartId = response.data.ChartId;
        } else if (response?.id) {
          chartId = response.id;
        } else if (this.currentChartId) {
          // Preserve existing chart ID when updating (API might not return it)
          chartId = this.currentChartId;
        }

        // Clean categories in response data (API uses PascalCase: Categories)
        const categories = chartDataResponse?.Categories || [];
        if (Array.isArray(categories) && categories.length > 0) {
          chartDataResponse.Categories = normalizeCategoryArray(categories);
        }

        // Clean categories in series data (API uses PascalCase: Series)
        const series = chartDataResponse?.Series || [];
        if (Array.isArray(series) && series.length > 0) {
          chartDataResponse.Series = series.map((seriesItem: any) => {
            if (seriesItem?.data && Array.isArray(seriesItem.data)) {
              // For pie charts, data might be in [name, value] format
              seriesItem.data = seriesItem.data.map((item: any) => {
                if (Array.isArray(item) && item.length >= 2) {
                  const copy = [...item];
                  copy[0] = normalizeChartDisplayLabel(copy[0]);
                  return copy;
                }
                if (item && typeof item === 'object' && !Array.isArray(item)) {
                  const next = { ...item };
                  if ('name' in next || 'label' in next) {
                    next.name = normalizeChartDisplayLabel(next.name ?? next.label ?? '');
                    if ('label' in next) {
                      next.label = next.name;
                    }
                  }
                  return next;
                }
                return item;
              }).filter((item: any) => {
                // Filter out items where the value is null/undefined
                if (Array.isArray(item) && item.length >= 2) {
                  return item[1] !== null && item[1] !== undefined;
                }
                return item !== null && item !== undefined;
              });
            }
            return seriesItem;
          });
        }

        // Extract chart_config from API response (API uses PascalCase: ChartConfig)
        const chartConfig = chartDataResponse?.ChartConfig || {};

        // Extract series, categories, and table_data from API response (all PascalCase)
        const rawCategories = chartDataResponse?.Categories || [];
        const rawSeries = chartDataResponse?.Series || [];
        const rawTableData = chartDataResponse?.TableData || [];
        const rawDescription = chartDataResponse?.Description || {};
        const rawMetricType = chartDataResponse?.MetricType;
        const rawTitle = chartDataResponse?.Title;

        // Extract axis labels from ChartConfig (API uses camelCase for nested objects: xAxis, yAxis)
        const xAxisLabel = chartConfig?.xAxis?.title?.text || '';
        const yAxisLabel = chartConfig?.yAxis?.title?.text || '';

        const chartDataObj: any = {
          series: rawSeries,
          categories: normalizeCategoryArray(rawCategories),
          table_data: rawTableData,
          description: rawDescription,
          metric_type: rawMetricType,
          xAxisLabel: xAxisLabel,
          yAxisLabel: yAxisLabel
        };

        // Validate that we have a valid API response (empty data is OK, but completely missing response is not)
        const hasValidResponse = chartDataResponse && typeof chartDataResponse === 'object';

        // Check if we have any actual chart data (this is informational, not an error condition)
        const hasChartData = hasValidResponse && (
          (chartDataObj.categories && chartDataObj.categories.length > 0) ||
          (chartDataObj.series && chartDataObj.series.length > 0 && chartDataObj.series.some((s: any) => s.data && s.data.length > 0)) ||
          (chartDataResponse.data && chartDataResponse.data.length > 0)
        );

        if (!hasValidResponse) {
          this.notificationService.error('Chart Generation Failed', 'Invalid response from server. Please try again.');
          this.isLoading = false;
          this.isSavingChart = false;
          return; // Don't add chart if response is invalid
        }

        // If there's no chart data, show a warning but still proceed (this is a valid scenario)
        if (!hasChartData) {
          // console.warn('Chart generated successfully but no data found for the given criteria');
        }

        // Extract title from API response (API uses PascalCase: Title)
        const chartTitle = this.chartTitle
          || chartDataResponse?.Title
          || chartConfig?.title?.text
          || this.selectedChartType!.name;

        // Extract chart_type (API uses PascalCase: ChartType)
        const chartType = chartDataResponse?.ChartType || this.selectedChartType!.id;

        const auditFields = extractChartAuditFields(chartDataResponse);
        const saveTimestamp =
          auditFields.last_edited_at
          || auditFields.updated_at
          || auditFields.last_updated_timestamp
          || new Date().toISOString();

        // Preserve filters for chart (same as Insights payload — FilterRequest[])
        const appliedFilters = (cleanedRequest.filters && Array.isArray(cleanedRequest.filters))
          ? [...cleanedRequest.filters]
          : [];

        const chartData: ChartData = {
          id: chartId, // Include ChartId from API response
          title: chartTitle,
          chart_type: chartType,
          data: chartDataObj,
          config: { ...chartConfig, filters: appliedFilters }, // Include filters in config for display
          table_data: chartDataObj.table_data, // Also preserve at top level for easy access
          ...auditFields,
          // Saving a chart (create or edit) regenerates data, so both timestamps move.
          last_updated_timestamp: saveTimestamp,
          last_edited_at: saveTimestamp,
          updated_at: saveTimestamp,
          time_zone: this.selectedTimezone?.trim() || auditFields.time_zone,
          DurationBehavior: chartDataResponse?.DurationBehavior,
          no_data_found: chartDataResponse?.NoDataFound ?? false,
          ...chartQueryFieldsFromApi(cleanedRequest),
          filters: appliedFilters // Top-level so chart-overview can display applied filters
        };

        // Show success notification
        const action = this.currentChartId ? 'updated' : 'generated';
        this.notificationService.success(
          this.currentChartId ? 'Chart Updated' : 'Chart Generated',
          `Chart ${action}.`
        );

        // Only emit chartAdded if we have valid data
        this.chartAdded.emit(chartData);
        this.isLoading = false;
        this.isSavingChart = false;
        this.resetForm();
        this.currentChartId = null; // Reset chart ID after successful save
      },
      error: (error: any) => {
        console.error('Error generating chart:', error);

        // Dismiss processing notification if still open
        processingNotification.dismiss();
        this.isSavingChart = false;

        // Check if it's a validation error about categories
        let errorTitle = 'Chart Generation Failed';
        let errorMessage = 'Unknown error occurred while generating chart. Please try again.';

        if (error?.error?.detail) {
          const detail = error.error.detail;
          if (typeof detail === 'string' && detail.includes('categories') && detail.includes('validation errors')) {
            errorTitle = 'Backend Validation Error';
            errorMessage = 'The server returned invalid data with null categories. This is a backend issue. Please try again or contact support.';
            console.error('Backend validation error - null categories in response:', detail);
          } else if (typeof detail === 'string') {
            // Try to extract a more user-friendly message from the detail
            if (detail.length > 200) {
              errorMessage = 'Server validation error occurred. Please check your input and try again.';
            } else {
              errorMessage = detail;
            }
          } else if (error.error.message) {
            errorMessage = error.error.message;
          }
        } else if (error?.error?.message) {
          errorMessage = error.error.message;
        } else if (error?.message) {
          errorMessage = error.message;
        } else if (error?.status === 0 || error?.status === 500) {
          errorTitle = 'Server Error';
          errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
        } else if (error?.status === 400) {
          errorTitle = 'Invalid Request';
          errorMessage = 'The request was invalid. Please check your input and try again.';
        } else if (error?.status === 401 || error?.status === 403) {
          errorTitle = 'Authentication Error';
          errorMessage = 'You are not authorized to perform this action. Please log in again.';
        }

        // Show error notification with regenerate chart message - DO NOT add/keep chart card on UI
        const isUpdate = !!this.currentChartId;
        const regenerateMessage = isUpdate
          ? 'Chart update failed. Please regenerate the chart.'
          : 'Chart generation failed. Please try again.';
        this.notificationService.error(errorTitle, regenerateMessage, 5000);
        this.isLoading = false;
        this.isSavingChart = false;
        // On update failure: notify parent to remove the chart card from the dashboard
        if (this.currentChartId) {
          this.chartUpdateFailed.emit(this.currentChartId);
        }
        // IMPORTANT: Do not emit chartAdded - chart should not be shown on API failure
      }
    });
  }

  onDateRangeTypeChange() {
    //console.log('DEBUG: onDateRangeTypeChange called - dateRangeType:', this.dateRangeType, 'isPopulatingForm:', this.isPopulatingForm);
    //console.log('DEBUG: Current dates before change - startDate:', this.startDate, 'endDate:', this.endDate);
    //console.log('DEBUG: Stack trace:', new Error().stack);

    // Don't clear dates during form population
    if (this.isPopulatingForm) {
      //console.log('DEBUG: Returning early due to isPopulatingForm=true');
      return;
    }

    // Reset last_n_days when switching away from last_n_days
    if (this.dateRangeType !== 'last_n_days') {
      this.lastNDays = null;
      // Remove last_n_days from criteria to prevent stale values
      if (this.criteria && this.criteria.last_n_days !== undefined) {
        delete this.criteria.last_n_days;
      }
    }

    // Clear custom dates only for new charts (not editing)
    // isPopulatingForm should handle this, but add extra safety check
    if (this.dateRangeType === 'custom_range' && !this.isEditMode && !this.isEditingChart && !this.isPopulatingForm) {
      //console.log('DEBUG: Clearing dates for new chart - isEditMode:', this.isEditMode, 'isEditingChart:', this.isEditingChart, 'isPopulatingForm:', this.isPopulatingForm);
      //console.log('DEBUG: About to clear dates - current dates:', this.startDate, this.endDate);
      this.startDate = '';
      this.endDate = '';
      //console.log('DEBUG: Dates after clearing - startDate:', this.startDate, 'endDate:', this.endDate);
    } else {
      //console.log('DEBUG: NOT clearing dates - dateRangeType:', this.dateRangeType, 'isEditMode:', this.isEditMode, 'isEditingChart:', this.isEditingChart, 'isPopulatingForm:', this.isPopulatingForm);
    }

    // Update date_range_type in criteria
    if (this.dateRangeType && this.dateRangeType !== 'custom_range') {
      this.criteria.date_range_type = this.dateRangeType;
    } else {
      // Remove date_range_type for custom_range
      if (this.criteria && this.criteria.date_range_type !== undefined) {
        delete this.criteria.date_range_type;
      }
    }
  }

  // Get display label for date range type
  getDateRangeDisplayLabel(dateRangeType: string): string {
    const labels: { [key: string]: string } = {
      'today': 'Today',
      'this_week': 'This Week',
      'this_month': 'This Month',
      'this_quarter': 'This Quarter',
      'this_year': 'This Year',
      'last_n_days': 'Last N Days'
    };
    return labels[dateRangeType] || dateRangeType;
  }

  onCancel() {
    this.resetForm();
    this.cancel.emit();
  }

  onClose() {
    this.close.emit();
  }

  resetForm() {
    this.isEditingChart = false; // Reset editing flag
    this.isEditMode = false; // Reset edit mode flag
    this.currentChartId = null; // Reset chart ID
    this.targetMetricType = null; // Clear target metric type
    this.existingChartRequest = null; // Clear existing chart request
    this.selectedChartType = null;
    this.selectedChartTypeId = '';
    this.selectedChartTypeRaw = null;
    this.chartTitle = '';
    this.selectedMetric = '';
    this.selectedDimension = '';
    this.selectedSecondaryDimension = null;
    this.selectedFilterField = '';
    this.selectedFilterOperator = '';
    this.selectedFilterValue = '';
    this.initialFilterConditions = [];
    this.optionalSectionExpanded = false;
    this.dateRangeType = 'today'; // Default to Today
    this.lastNDays = null;
    this.selectedTimezone = 'EST';
    this.selectedTimezoneOption = this.timezoneOptions.length ? (this.timezoneOptions.find(o => o.value === 'EST') ?? this.timezoneOptions[0]) : null;
    // Set default dates (for non-custom range)
    this.startDate = dayjs().subtract(7, 'days').format('YYYY-MM-DD');
    this.endDate = dayjs().format('YYYY-MM-DD');
    this.criteria = {
      top_n: 10,
      time_bucket: 'day',
      sort_order: 'desc',
      show_percentage: true
    };
    this.metrics = [];
    this.criteriaOptions = [];
  }

  /** Default min slice % for pie/donut when unset or invalid (user may still enter 0). */
  private readonly defaultPieDonutMinSlicePercentage = 1;

  /**
   * Pie/donut: labels on by default; min slice is edited only under Chart-Specific Options.
   * Normalizes min_slice_percentage to [0, 10], defaulting missing/invalid values to 1.
   */
  ensurePercentageForPieDonutCharts() {
    if (!this.isPieOrDonutChart()) {
      return;
    }
    this.criteria.show_percentage = true;
    const raw = this.criteria.min_slice_percentage;
    if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
      this.criteria.min_slice_percentage = this.defaultPieDonutMinSlicePercentage;
      return;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n)) {
      this.criteria.min_slice_percentage = this.defaultPieDonutMinSlicePercentage;
      return;
    }
    this.criteria.min_slice_percentage = Math.min(10, Math.max(0, n));
  }

  private isMinSlicePercentageValid(): boolean {
    if (!this.isPieOrDonutChart()) {
      return true;
    }
    const raw = this.criteria.min_slice_percentage;
    if (raw === null || raw === undefined || (typeof raw === 'string' && String(raw).trim() === '')) {
      return false;
    }
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n) || n < 0 || n > 10) {
      return false;
    }
    return true;
  }

  onMinSlicePercentageInput(event: Event): void {
    if (!this.isPieOrDonutChart()) {
      return;
    }
    const input = event.target as HTMLInputElement;
    if (input.value === '' || input.value === '-') {
      return;
    }
    const n = parseFloat(input.value);
    if (Number.isNaN(n)) {
      return;
    }
    if (n < 0) {
      this.criteria.min_slice_percentage = 0;
    } else if (n > 10) {
      this.criteria.min_slice_percentage = 10;
    }
  }

  onMinSlicePercentageBlur(): void {
    if (!this.isPieOrDonutChart()) {
      return;
    }
    this.ensurePercentageForPieDonutCharts();
  }

  getCategoryKeys(): string[] {
    return Object.keys(this.chartCategories);
  }

  // Helper methods for chart-specific criteria
  isPieOrDonutChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'pie' || chartTypeId === 'donut';
  }

  isBubbleOrScatterChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'bubble' || chartTypeId === 'scatter';
  }

  isTreemapChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'treemap';
  }

  isSankeyChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'sankey';
  }

  isWaterfallChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'waterfall';
  }

  isPolarOrRadarChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'polar' || chartTypeId === 'radar';
  }

  isGaugeChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'gauge';
  }

  isHeatmapChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'heatmap';
  }

  hasChartSpecificOptions(): boolean {
    if (!this.selectedChartType) {
      return false;
    }

    return (
      this.isPieOrDonutChart() ||
      this.isBubbleOrScatterChart() ||
      this.isTreemapChart() ||
      this.isSankeyChart() ||
      this.isWaterfallChart() ||
      this.isPolarOrRadarChart() ||
      this.isGaugeChart() ||
      this.isHeatmapChart()
    );
  }

  isDonutChart(): boolean {
    const chartTypeId = this.selectedChartTypeId?.toLowerCase() || '';
    return chartTypeId === 'donut';
  }

  getValidCriteriaOptions(): CriteriaOption[] {
    // Filter out API response wrapper fields
    const excludedFields = ['id', 'object', 'created', 'data', 'success', 'message', 'detail'];
    return this.criteriaOptions.filter(opt => {
      const fieldLower = opt.field.toLowerCase();
      if (excludedFields.includes(fieldLower) || fieldLower === 'response' || opt.field === 'Response') {
        return false;
      }
      // Pie/donut: min slice is only in Chart-Specific Options (avoid duplicate with API criteria field)
      if (this.isPieOrDonutChart() && fieldLower === 'min_slice_percentage') {
        return false;
      }
      return true;
    });
  }

  // Helper methods for conditional form field display
  shouldShowDimensionField(): boolean {
    if (!this.selectedChartType) {
      return false;
    }

    const chartTypeId = this.selectedChartType.id.toLowerCase();
    const requiredDimensions = this.selectedChartTypeRaw?.required_dimensions || 0;

    // Charts that don't need dimensions (like gauge)
    const chartsWithoutDimensions = ['gauge'];

    // Always show dimension field unless it's a chart type that explicitly doesn't need it
    // But hide it if required_dimensions is 0 and it's a known chart type that doesn't need dimensions
    if (chartsWithoutDimensions.includes(chartTypeId) && requiredDimensions === 0) {
      return false;
    }

    return true;
  }

  isDimensionRequired(): boolean {
    if (!this.selectedChartTypeRaw) {
      return false;
    }
    const requiredDimensions = this.selectedChartTypeRaw.required_dimensions || 0;
    return requiredDimensions > 0;
  }

  shouldShowSecondaryDimensionField(): boolean {
    if (!this.selectedChartType) {
      return false;
    }

    const chartTypeId = this.selectedChartType.id.toLowerCase();
    const requiredDimensions = this.selectedChartTypeRaw?.required_dimensions || 0;

    // Only show secondary dimension for heatmap charts
    if (chartTypeId === 'heatmap') {
      return true;
    }

    // Also show if API explicitly requires 2 dimensions
    if (requiredDimensions === 2) {
      return true;
    }

    return false;
  }

  isSecondaryDimensionRequired(): boolean {
    if (!this.selectedChartTypeRaw) {
      return false;
    }
    const requiredDimensions = this.selectedChartTypeRaw.required_dimensions || 0;
    return requiredDimensions === 2;
  }

  // Form validation helper
  isFormValid(): boolean {
    // Basic required fields
    if (!this.selectedChartType || !this.selectedMetric || !this.chartTitle || this.chartTitle.trim() === '') {
      return false;
    }

    // Date range validation - either custom_range dates or dynamic date range
    if (this.dateRangeType && this.dateRangeType !== 'custom_range') {
      // Dynamic date range selected
      if (this.dateRangeType === 'last_n_days') {
        if (!this.lastNDays || this.lastNDays < 1) {
          return false;
        }
      }
      // Other dynamic ranges don't need additional validation
    } else {
      // Custom date range selected
      if (!this.startDate || !this.endDate) {
        return false;
      }

      if (!checkCustomDateRange(this.startDate, this.endDate).valid) {
        return false;
      }
    }

    if (!this.isMinSlicePercentageValid()) {
      return false;
    }

    // Dimension validation based on chart type
    const requiredDimensions = this.selectedChartTypeRaw?.required_dimensions || 0;
    const chartTypeId = this.selectedChartType.id.toLowerCase();
    const chartsWithoutDimensions = ['gauge'];

    // Check primary dimension requirement
    if (requiredDimensions > 0 && !this.selectedDimension) {
      return false;
    }

    // Check secondary dimension requirement
    if (requiredDimensions === 2) {
      if (!this.selectedSecondaryDimension || this.selectedSecondaryDimension === '' || this.selectedSecondaryDimension === null) {
        return false;
      }
      // Ensure secondary dimension is different from primary
      if (this.selectedSecondaryDimension === this.selectedDimension) {
        return false;
      }
    }

    return true;
  }

  // Date change handlers for real-time validation
  onStartDateChange(date: string) {
    if (!date) return;

    // Don't override dates during form population and shortly after
    if (this.isPopulatingForm) return;

    // Add additional protection for Angular change detection cycles
    const now = Date.now();
    if (this._lastFormPopulationTime && (now - this._lastFormPopulationTime) < 1000) {
      return;
    }

    const start = dayjs(date);
    const minDate = dayjs('2023-01-01');

    // Prevent dates before 2023-01-01
    if (start.isBefore(minDate, 'day')) {
      this.notificationService.error('Invalid Date', 'Start date cannot be before January 1, 2023.');
      this.startDate = minDate.format('YYYY-MM-DD');
      return;
    }

    // Future start dates are allowed. Keep the end date consistent with the new start date.
    if (this.endDate) {
      const end = dayjs(this.endDate);
      if (end.isBefore(start, 'day')) {
        this.endDate = start.format('YYYY-MM-DD');
      } else if (end.diff(start, 'day') > CUSTOM_RANGE_MAX_DAYS) {
        this.notificationService.error('Invalid Date', `Custom range cannot exceed ${CUSTOM_RANGE_MAX_DAYS} days. End date was adjusted.`);
        this.endDate = this.maxEndDate;
      }
    }
  }

  onEndDateChange(date: string) {
    if (!date) return;

    // Don't override dates during form population
    if (this.isPopulatingForm) return;

    const end = dayjs(date);

    // Future end dates are allowed; only order and the 90-day span are enforced.
    if (this.startDate) {
      const start = dayjs(this.startDate);
      if (end.isBefore(start, 'day')) {
        this.notificationService.error('Invalid Date', 'End date must be after start date.');
        this.endDate = start.format('YYYY-MM-DD');
      } else if (end.diff(start, 'day') > CUSTOM_RANGE_MAX_DAYS) {
        this.notificationService.error('Invalid Date', `Custom range cannot exceed ${CUSTOM_RANGE_MAX_DAYS} days.`);
        this.endDate = this.maxEndDate;
      }
    }
  }

  // Input validation methods for Top N field
  onTopNKeyPress(event: KeyboardEvent): boolean {
    // Allow only digits (0-9)
    const charCode = event.charCode;
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }
    // Prevent all other characters including -, +, ., and letters
    event.preventDefault();
    return false;
  }

  onTopNInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Remove any non-digit characters
    input.value = input.value.replace(/[^0-9]/g, '');

    // Update the model
    const fieldName = input.getAttribute('data-field') || 'top_n';
    this.criteria[fieldName] = input.value ? parseInt(input.value, 10) : null;
  }
}
